import * as React from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import Modal from 'react-bootstrap/Modal';
import JobTabsData from './JobTabs';
import type { GenericList, EditNoteItem, ItemOptionList } from './models/JobSharedInterfaces';
import type { JobQuoteDetails, JobQuoteItems, NewJobQuoteItemExtra, JobQuoteExtraItemOption, JobQuoteExtraSection, DeliveryAddress } from './models/JobInterfaces';
import NotesPopup from './NotesPopup';
import JobQuoteCalculatorModal from './JobQuoteCalculatorModal';
import JobQuoteWalkWayModal from './JobQuoteWalkWayModal';
import QuoteSiteNotePopup from './JobQuoteSiteNoteModal';
import QuoteNotePopup from './JobQuoteNoteModal';
import ConfirmPopups from './ConfirmPopup';
import JobQuoteExtraItemModal from './JobQuoteExtraItemModal';
import JobQuoteExtraSectionData from './JobQuoteExtraSectionModal';
import AllocateScheduleJobPopup from './AllocateScheduleJobModal';
import JobQuoteQuestionsData from './JobQuoteQuestions';
import JobQuoteExtraTrolleyData from './JobQuoteExtraTrolleyModal';
import JobQuoteExtraInvoiceItemModal from './JobQuoteExtraInvoiceItemsModal';
import JobQuoteDeliveryAddressData from './JobQuoteDeliveryAddressModal';
import JobQuoteBraceModal from './JobQuoteBraceModal';
import JobQuoteEpaQuestionsModalData from './JobQuoteEpaQuestionsModal';
import { DownloadFile, GetQuery, PostFormDataQuery, PostQuery } from './Shared';
import type { PlanFile } from './models/DriverJobInterfaces';

interface ScheduleItemDetail {
    jobId: number;
    jobQuoteItemId: number;
    item: string;
    itemTypeId: number;
    installOnly: boolean;
    isEvent: boolean;
    needsPickup: boolean;
    hirePeriod: number;
    frequencyId: number;
    customerAccountTypeId: number;
    customerLinkedToReckon: boolean;
    siteContact: string;
    jobOnHold: boolean;
}

interface QuoteItemNotes {
    id: number;
    jobId: number;
    itemId: number;
    notes: string;
    quoteNote: string;
};

interface KwikstageItem {
    extraHours: number;
    kwikstageInstalledFor: number;
    quoteNote: string;
}

interface KwikstageCalculation {
    id: number;
    starterBayId: number;
    liftId: number;
    bays: number;
    baysRemaining: number;
}

interface WalkWayItem {
    id: number;
    steepRoofPitch: number;
    quantity: number;
    extraHours: number;
    quoteNote: string;
}

interface RateGst {
    rate: number,
    gst: number
};


const JobQuoteData = () => {
    let navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [systemCompanyId, setSystemCompanyId] = React.useState(2);
    const [chargeFuelSurcharge, setChargeFuelSurcharge] = React.useState(false);
    const [useCustomPriceList, setUseCustomPriceList] = React.useState(false);
    const [showCancelledItems, setShowCancelledItems] = React.useState(false);
    const [plans, setPlans] = React.useState<PlanFile[]>([]);
    const [showDeletePlan, setShowDeletePlan] = React.useState(false);
    const [deletePlanId, setDeletePlanId] = React.useState(0);
    const [itemOptions, setItemOptions] = React.useState<ItemOptionList[]>([]);
    const [frequencyOptions, setFrequencyOptions] = React.useState<GenericList[]>([]);
    const [cancelOptions, setCancelOptions] = React.useState<GenericList[]>([]);
    const [starterBayOptions, setStarterBayOptions] = React.useState<GenericList[]>([]);
    const [liftOptions, setLiftOptions] = React.useState<GenericList[]>([]);
    const [kwikstageInstalledForOptions, setKwikstageInstalledForOptions] = React.useState<GenericList[]>([]);
    const [yesNoOptions, setYesNoOptions] = React.useState<GenericList[]>([]);
    const [truckOptions, setTruckOptions] = React.useState<GenericList[]>([]);
    const [truckSideOptions, setTruckSideOptions] = React.useState<GenericList[]>([]);
    const [towerSizeOptions, setTowerSizeOptions] = React.useState<GenericList[]>([]);
    const [cancelItem, setCancelItem] = React.useState("");
    const [cancelItemIndexId, setCancelItemIndexId] = React.useState(-1);
    const [cancelReasonId, setCancelReasonId] = React.useState(-1);
    const [showCancelModal, setShowCancelModal] = React.useState(false);
    const [gst, setGst] = React.useState(0);
    const [showEditNotesModal, setShowEditNotesModal] = React.useState(false);
    const [editNotes, setEditNotes] = React.useState<EditNoteItem>({
        heading: "",
        rowIndex: 0,
        parentIndex: 0,
        id: 0,
        item: "",
        itemId: 0,
        notes: ""
    });
    const [showCalculator, setShowCalculator] = React.useState(false);
    const [calculatorIndex, setCalculatorIndex] = React.useState(-1);
    const [calculatorKwikstageItem, setCalculatorKwikstageItem] = React.useState<KwikstageItem>({
        extraHours: 0,
        kwikstageInstalledFor: -1,
        quoteNote: ""
    });
    const [calculatorItems, setCalculatorItems] = React.useState<KwikstageCalculation[]>([]);
    const [calculatorStatusId, setCalculatorStatusId] = React.useState(0);
    const [walkWayItem, setWalkWayItem] = React.useState<WalkWayItem>({
        id: 0,
        quantity: 0,
        steepRoofPitch: 0,
        extraHours: 0,
        quoteNote: ""
    });
    const [showWalkwayPitch, setShowWalkwayPitch] = React.useState(false);
    const [walkwayIndexId, setWalkwayIndexId] = React.useState(-1);
    const [walkwayStatusId, setWalkwayStatusId] = React.useState(-1);
    const [showQuoteNote, setShowQuoteNote] = React.useState(false);
    const [quoteNoteIndex, setQuoteNoteIndex] = React.useState(-1);
    const [quoteNote, setQuoteNote] = React.useState("");
    const [showAddSiteNotes, setShowAddSiteNotes] = React.useState(false);
    const [siteNotes, setSiteNotes] = React.useState<GenericList[]>([]);
    const [saveDisabled, setSaveDisabled] = React.useState(false);
    const [siteAddress, setSiteAddress] = React.useState("");
    const [jobTypeId, setJobTypeId] = React.useState(1);
    const [extraItemOptions, setExtraItemOptions] = React.useState<JobQuoteExtraItemOption[]>([]);
    const [showExtraItems, setShowExtraItems] = React.useState(false);
    const [editExtraItems, setEditExtraItems] = React.useState(false);
    const [extraIndex, setExtraIndex] = React.useState(-1);
    const [extras, setExtras] = React.useState<NewJobQuoteItemExtra>({
        itemId: -1,
        quantity: 0,
        equipped: false
    });
    const [itemDescription, setItemDescription] = React.useState("");
    const [tables, setTables] = React.useState(1);
    const [chairs, setChairs] = React.useState(2);
    const [lunchRoomExtraId, setLunchRoomExtraId] = React.useState(0);
    const [siteOfficeExtraId, setSiteOfficeExtraId] = React.useState(0);
    const [showExtraSection, setShowExtraSection] = React.useState(false);
    const [extraSection, setExtraSection] = React.useState<JobQuoteExtraSection>({
        totalLength: 0,
        crossoverQuantity: 0,
        sectionQuantity: 0,
        totalCrossoverLength: "0.0"
    });
    const [showScheduleJob, setShowScheduleJob] = React.useState(false);
    const initScheduleItemDetail: ScheduleItemDetail = {
        jobId: 0,
        jobQuoteItemId: 0,
        item: "",
        itemTypeId: 0,
        installOnly: false,
        isEvent: false,
        needsPickup: false,
        hirePeriod: 0,
        frequencyId: 0,
        customerAccountTypeId: 0,
        customerLinkedToReckon: true,
        siteContact: "",
        jobOnHold: false
    };
    const [scheduleItemDetail, setScheduleItemDetail] = React.useState<ScheduleItemDetail>(initScheduleItemDetail);
    const [scheduleJobNeedsPickup, setScheduleJobNeedsPickup] = React.useState(false);
    const [customerId, setCustomerId] = React.useState(0);
    const [customerAccountTypeId, setCustomerAccountTypeId] = React.useState(0);
    const [customerAccountType, setCustomerAccountType] = React.useState("");
    const [customerAccountLimitFormatted, setCustomerAccountLimitFormatted] = React.useState("$0.00");
    const [customerLinkedToReckon, setCustomerLinkedToReckon] = React.useState(true);
    const [customerNewFuelInvoice, setCustomerNewFuelInvoice] = React.useState(false);
    const [showPowerPoleQuestions, setShowPowerPoleQuestions] = React.useState(false);
    const [powerPoleItemId, setPowerPoleItemId] = React.useState(-1);
    const [powerPoleCanEdit, setPowerPoleCanEdit] = React.useState(false);
    const [showEpaQuestions, setShowEpaQuestions] = React.useState(false);
    const [itemTypeId, setItemTypeId] = React.useState(-1);
    const [showExtraTrolley, setShowExtraTrolley] = React.useState(false);
    const [extraTrolley, setExtraTrolley] = React.useState(false);
    const [showExtraInvoiceItems, setShowExtraInvoiceItems] = React.useState(false);
    const [extraInvoiceItemQuoteId, setExtraInvoiceItemQuoteId] = React.useState(0);
    const [showInvoiceDescription, setShowInvoiceDescription] = React.useState(false);
    const [jobQuoteItemId, setJobQuoteItemId] = React.useState(0);
    const [itemId, setItemId] = React.useState(0);
    const [existInvoiceDescription, setExistInvoiceDescription] = React.useState("");
    const [trolley, setTrolley] = React.useState(false);
    const [editTrolleyReadOnly, setEditTrolleyReadOnly] = React.useState(false);
    const [showFenceBracing, setShowFenceBracing] = React.useState(false);
    const [fenceArea, setFenceArea] = React.useState("");
    const [epaTruckHireRate, setEpaTruckHireRate] = React.useState(0);
    const [epaConfinedSpacePrice, setEpaConfinedSpacePrice] = React.useState(0);
    const [weekendRate, setWeekendRate] = React.useState(0);
    const [sameDayRate, setSameDayRate] = React.useState(0);
    const initQuoteDetails: JobQuoteDetails = {
        id: 0,
        jobId: Number(id),
        quoteNo: 0,
        manualQuoteNumber: "",
        isEvent: false,
        requireSiteVisit: false,
        siteVisitPerson: -1,
        siteContactName: "",
        siteContactPhone: "",
        showSiteVisitNote: true,
        totalQuotedAmount: 0,
        totalQuotedAmountFormatted: "$0.00",
        totalInvoiceExtraAmount: 0,
        totalInvoiceExtraAmountFormatted: "$0.00",
        totalInvoiceAmount: 0,
        totalInvoiceAmountFormatted: "$0.00",
        quoteNotes: "",
        siteAddressDuration: 0,
        customerPickingUp: 0,
        isDirty: false,
        jobQuoteItems: [{
            itemIndex: 0,   //used to update items in the array as may have multiple items with id of 0
            id: 0,
            itemId: -1,
            item: "",
            itemDescription: "",
            installOnly: false,
            extendable: true,
            invoiceDescription: "",
            typeId: -1,
            productGroupId: 0,
            steepRoofPitch: 0,
            isWalkWay: false,
            isVoid: false,
            isTower: false,
            unitOfMeasure: "",
            status: "Quoted",
            statusId: 1,
            hirePeriod: 0,
            defaultHirePeriod: 0,
            customHirePeriod: false,
            hirePeriodWeeksDesc: "",
            frequencyId: -1,
            locationId: -1,
            fenceBraceId: -1,
            size: 0,
            includeInstallDismantle: 0,
            quantity: 0,
            initialHire: 0,
            travelAmount: 0,
            cleaningFee: 0,
            trenchFee: 0,
            confinedSpaceAmount: 0,
            sampleTestingAmount: 0,
            wasteDisposalAmount: 0,
            fuelSurchargeAmount: 0,
            extraHours: 0,
            kwikstageInstalledForId: -1,
            rate: 0,
            gst: 0,
            amount: 0,
            overhireRate: 0,
            estCostPriceFormatted: "$0.00",
            estMarginFormatted: "$0.00",
            estMarginPercentageFormatted: "0%",
            callout: false,
            notes: "",
            quoteNote: "",
            jobQuoteDescription: "",
            jobQuoteInstalledFor: "",
            jobQuoteHeader: "",
            jobQuoteFloor: "",
            linkedId: 0,
            canEditTravel: true,
            itemOrder: 1,
            showOnQuote: true,
            isEws: false,
            isEvent: false,
            truckId: -1,
            truckDoorsId: -1,
            trolley: false,
            extras: {
                itemId: -1,
                quantity: 0,
                equipped: false
            },
            incStepsTravel: false,
            invoicePeriodTypeId: 1,
            tankLidExposed: false,
            weekendJob: false,
            emergencySameDayJob: false,
            epaQuestionsAnswered: false,
            isDirty: false
        }]
    };
    const [quoteDetails, setQuoteDetails] = React.useState<JobQuoteDetails>(initQuoteDetails);
    const [updateQuote, setUpdateQuote] = React.useState(false);

    React.useEffect(() => {
        window.scrollTo(0, 0);
        getData();
    }, []);

    React.useEffect(() => {
        if (updateQuote) {
            getQuote();
        }
    }, [updateQuote])

    const getData = async () => {
        try {
            const apiUrl = 'GetQuoteDropdowns?JobId=' + id;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                setSystemCompanyId(result.data.systemCompanyId);
                setChargeFuelSurcharge(result.data.chargeFuelSurcharge);
                setSiteAddress(result.data.siteAddress);
                setJobTypeId(result.data.jobType);
                setExtraItemOptions(result.data.extraItemOptions);
                setSiteOfficeExtraId(result.data.siteOfficeExtraId);
                setLunchRoomExtraId(result.data.lunchRoomExtraId);
                setItemOptions(result.data.jobQuoteItemList);
                setFrequencyOptions(result.data.frequencyOptions);
                setGst(result.data.gst);
                setYesNoOptions(result.data.yesNoOptions);
                setUseCustomPriceList(result.data.useCustomPriceList);
                setLiftOptions(result.data.liftOptions);
                setStarterBayOptions(result.data.starterBayOptions,);
                setScheduleJobNeedsPickup(!result.data.allowOnGoingHire);
                setKwikstageInstalledForOptions(result.data.kwikstageInstalledForOptions);
                setTruckOptions(result.data.truckOptions);
                setTruckSideOptions(result.data.truckSideOptions);
                setTowerSizeOptions(result.data.towerSizeOptions);
                setCustomerId(result.data.customerId);
                setCustomerAccountTypeId(result.data.accountTypeId);
                setCustomerAccountType(result.data.accountType);
                setCustomerAccountLimitFormatted(result.data.accountLimitFormatted);
                setCustomerLinkedToReckon(result.data.customerLinkedToReckon);
                setCustomerNewFuelInvoice(result.data.customerNewFuelInvoice);
                setEpaTruckHireRate(result.data.epaTruckHireRate);
                setEpaConfinedSpacePrice(result.data.epaConfinedSpacePrice);
                setWeekendRate(result.data.weekendRate);
                setSameDayRate(result.data.sameDayRate);
                setUpdateQuote(true);
            }
        } catch (e) {
            toast.error("Error getting quote details!");
        }
    }

    const getQuote = async () => {
        try {
            const apiUrl = "GetQuote?JobId=" + id;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                if (result.data.jobQuote.quoteNo) {
                    var quote: JobQuoteDetails = result.data.jobQuote;
                    if (!quote.requireSiteVisit) {
                        quote.siteVisitPerson = -1;
                    }
                    //add item index to quote items
                    for (var i = 0; i < quote.jobQuoteItems.length; i++) {
                        quote.jobQuoteItems[i].itemIndex = i;
                        var quoteItem = quote.jobQuoteItems[i];
                        quote.jobQuoteItems[i].hirePeriod = quoteItem.customHirePeriod && quoteItem.invoicePeriodTypeId === 1 ? quoteItem.hirePeriod / 7 : quoteItem.hirePeriod;
                    }

                    //add new row to quote detail items
                    if (jobTypeId === 1 || jobTypeId === 3) {
                        let filteredItems = quote.jobQuoteItems.filter(data =>
                            data.status != "Cancelled"
                        );
                        var blankLine = addBlankLine(quote.jobQuoteItems.length, filteredItems.length);
                        quote.jobQuoteItems.push(blankLine);
                    }

                    setQuoteDetails(quote);
                    setPlans(result.data.plans);
                    setUpdateQuote(false);
                    setLoading(false);
                    setSaveDisabled(false);
                } else {
                    setUpdateQuote(false);
                    setLoading(false);
                    setSaveDisabled(false);
                }
            }
        } catch (e) {
            toast.error("Error getting quote details!");
        }
        
    }

    const getQuotePlans = async () => {
        try {
            const apiUrl = "GetJobQuotePlans?JobId=" + id;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                setPlans(result.data);
            }
        } catch (e) {
            toast.error("Error getting quote plans!");
        }
    }

    const addBlankLine = (itemIndex: number, itemOrder: number) => {
        var qty = systemCompanyId === 3 ? 1 : 0;     //may need to change this for fencing

        var newLine: JobQuoteItems = {
            itemIndex: itemIndex,
            id: 0,
            itemId: -1,
            item: "",
            itemDescription: "",
            installOnly: false,
            extendable: true,
            invoiceDescription: "",
            typeId: -1,
            productGroupId: 0,
            steepRoofPitch: 0,
            isWalkWay: false,
            isVoid: false,
            isTower: false,
            unitOfMeasure: "",
            status: "Quoted",
            statusId: 1,
            hirePeriod: 0,
            defaultHirePeriod: 0,
            customHirePeriod: false,
            hirePeriodWeeksDesc: "",
            frequencyId: -1,
            locationId: -1,
            fenceBraceId: -1,
            size: 0,
            includeInstallDismantle: 0,
            quantity: qty,
            initialHire: 0,
            travelAmount: 0,
            cleaningFee: 0,
            trenchFee: 0,
            confinedSpaceAmount: 0,
            sampleTestingAmount: 0,
            wasteDisposalAmount: 0,
            fuelSurchargeAmount: 0,
            extraHours: 0,
            kwikstageInstalledForId: -1,
            rate: 0,
            gst: 0,
            amount: 0,
            overhireRate: 0,
            estCostPriceFormatted: "$0.00",
            estMarginFormatted: "$0.00",
            estMarginPercentageFormatted: "0%",
            callout: false,
            notes: "",
            quoteNote: "",
            jobQuoteDescription: "",
            jobQuoteInstalledFor: "",
            jobQuoteHeader: "",
            jobQuoteFloor: "",
            linkedId: 0,
            canEditTravel: true,
            itemOrder: itemOrder + 1,
            showOnQuote: true,
            isEws: false,
            isEvent: false,
            truckId: -1,
            truckDoorsId: -1,
            trolley: false,
            extras: {
                itemId: -1,
                quantity: 0,
                equipped: false
            },
            incStepsTravel: false,
            invoicePeriodTypeId: 1,
            tankLidExposed: false,
            weekendJob: false,
            emergencySameDayJob: false,
            epaQuestionsAnswered: false,
            isDirty: false
        };

        return newLine;
    }

    const clearErrors = (errorName: string) => {
        let errorList = errors;
        if (errorName) {
            delete errorList[errorName];
            setErrors(errorList);
        }
    }

    //history screen
    const viewQuoteHistory = (e: any) => {
        e.preventDefault();
        var url = "/job/" + id + "/quotehistory";
        navigate(url);
    }
    //end history

    const handleChange = (e: any) => {
        clearErrors(e.target.name);

        setQuoteDetails(prevState => ({ ...prevState, [e.target.name]: e.target.value, isDirty: true }));
        setSaveDisabled(false);
    }

    const handleCustPickupChange = (e: any) => {
        clearErrors(e.target.name);

        let value = e.target.value * 1;
        setQuoteDetails(prevState => ({ ...prevState, [e.target.name]: e.target.value, isDirty: true }));

        //update travel to $0 if customer picking up otherwise re-calculate price
        let quoteItems: JobQuoteItems[] = [...quoteDetails.jobQuoteItems];
        for (var i = 0; i < quoteItems.length; i++) {
            let quoteItem = quoteItems[i];
            if (value === 1) {
                quoteItem.travelAmount = 0;
                quoteItem.amount = quoteItem.initialHire + quoteItem.cleaningFee + quoteItem.trenchFee;
                setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
            } else {
                calcQuotePrice(quoteItems, quoteItem);
            }
        }
        setSaveDisabled(false);
    }

    const handleSiteVisitCheckboxChange = (e: any) => {
        setQuoteDetails(prevState => ({ ...prevState, requireSiteVisit: e.target.checked, siteVisitPerson: -1, isDirty: true }));
        setSaveDisabled(false);
    }

    const handleSiteVisitNoteChange = (e: any) => {
        setQuoteDetails(prevState => ({ ...prevState, showSiteVisitNote: e.target.checked }));
        setSaveDisabled(false);
    }

    //WALKWAY
    const checkWalkWayPitch = (e: any, itemIndex: number) => {
        e.preventDefault();
        var quoteItem = quoteDetails.jobQuoteItems[itemIndex];

        if (quoteItem.isWalkWay) {
            //check pitch
            var walkWay: WalkWayItem = {
                id: quoteItem.id,
                quantity: quoteItem.quantity,
                steepRoofPitch: quoteItem.steepRoofPitch,
                extraHours: quoteItem.extraHours,
                quoteNote: quoteItem.quoteNote
            };
            setWalkWayItem(walkWay);
            setShowWalkwayPitch(true)
            setWalkwayStatusId(quoteItem.statusId);
            setWalkwayIndexId(itemIndex);
            setSaveDisabled(false);
        } else if (quoteItem.typeId === 27) {
            calculateCrossoverQuantity(e, itemIndex);
        }
    }

    const hideWalkWayPitch = () => {
        let walkWay = resetWalkWayItem();
        setWalkWayItem(walkWay);
        setShowWalkwayPitch(false);
        setWalkwayStatusId(0);
        setWalkwayIndexId(-1);
    }

    const updateWalkWayPitch = (updatedWalkWayItem: WalkWayItem) => {
        setWalkWayItem(prevState => ({ ...prevState, updatedWalkWayItem }));
    }

    const resetWalkWayItem = () => {
        var walkWay: WalkWayItem = {
            id: 0,
            quantity: 0,
            steepRoofPitch: 0,
            extraHours: 0,
            quoteNote: ""
        };

        return walkWay;
    }

    const saveWalkWayPitch = () => {
        saveWalkWayPitchItem();
    }

    const saveWalkWayPitchItem = async () => {
        var quote = quoteDetails;
        var quoteItems = [...quote.jobQuoteItems];
        var quoteItem = quoteItems[walkwayIndexId];
        //update quote item from walkway options;
        quoteItem.quantity = walkWayItem.quantity;
        quoteItem.extraHours = walkWayItem.extraHours;
        quoteItem.steepRoofPitch = walkWayItem.steepRoofPitch;
        quoteItem.quoteNote = walkWayItem.quoteNote;

        //calc price
        var itemId = quoteItem.itemId * 1;
        var chargeGst = itemChargeGst(itemId);

        var wwItem = {
            jobId: id,
            customerId: customerId,
            useCustomPriceList: useCustomPriceList,
            chargeGst: chargeGst,
            walkWayItem: quoteItem,
            jobQuoteId: quote.quoteNo,
            itemOrder: quoteItem.itemOrder
        };

        try {
            var jsonItem = JSON.stringify(wwItem);

            const apiUrl = "CalculateWalkWayQuotePrice";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                let newquoteItem = result.data.walkWayItem;
                newquoteItem.itemIndex = walkwayIndexId;
                quoteItems.splice(walkwayIndexId, 1, newquoteItem);
                var wwRaise = result.data.walkWayRaiseItem;
                if (wwRaise) {
                    //check if wwRaise already exists and just needs updating
                    var existIndex = quoteItems.findIndex(jqi => jqi.id === wwRaise.id);
                    if (existIndex < 0 || wwRaise.id === 0) {
                        //add new raise
                        //insert the raise before the blank new row
                        var indexToInsert = quoteItems.length - 1;
                        wwRaise.itemIndex = indexToInsert;
                        indexToInsert = indexToInsert < 0 ? 0 : indexToInsert;
                        quoteItems.splice(indexToInsert, 0, wwRaise);
                        //update blank new row with next itemindex
                        var blankRowIndex = quoteItems.length - 1;
                        var blankRow = quoteItems[blankRowIndex];
                        if (blankRow) {
                            blankRow.itemIndex = (indexToInsert + 1);
                            blankRow.itemOrder = wwRaise.itemOrder + 1;
                        }
                    } else {
                        //update existing raise
                        quoteItems.splice(existIndex, 1, wwRaise);
                    }
                }
                
                let resetWWItem = resetWalkWayItem();
                
                setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
                setWalkWayItem(resetWWItem);
                setShowWalkwayPitch(false);
                setWalkwayStatusId(0);
                setWalkwayIndexId(-1);
            }
        } catch (e) {
            toast.error("Error saving job!");
        }     
    }
    //END WALKWAY

    //FENCE BRACING
    const openFenceBracing = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        var quoteItems = quote.jobQuoteItems;
        var quoteItem = quoteItems[itemIndex];
        var itemStatusId = quoteItem.statusId;
        setShowFenceBracing(true);
        setCalculatorIndex(itemIndex);
        setCalculatorStatusId(itemStatusId);
    }

    const hideFenceBracing = () => {
        setShowFenceBracing(false);
        setCalculatorIndex(-1);
        setCalculatorStatusId(0);
    }

    const saveFenceBracing = (fenceQuantity: number, braceQuantity: number, fenceBraceId: number, fenceNote: string, fenceAreaNote: string) => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[calculatorIndex];

        //editing the brace item so update based on items selected
        if (quoteItem.typeId === 51) {
            quoteItem.quantity = braceQuantity;
            quoteItem.fenceBraceId = fenceBraceId;
            quoteItem.notes += fenceNote;
            setShowFenceBracing(false);
            setFenceArea(fenceAreaNote);
            setCalculatorIndex(-1);
            setCalculatorStatusId(0);
            setSaveDisabled(false);

            //calculate the price for the braces
            calcQuotePrice(quoteItems, quoteItem);
        } else {
            //editing fencing
            quoteItem.quantity = fenceQuantity;
            quoteItem.notes += fenceNote;
            calcFencePrice(quoteItems, quoteItem, braceQuantity, fenceBraceId, fenceNote, fenceAreaNote);
        }
    }

    const calcFencePrice = async (quoteItems: JobQuoteItems[], quoteItem: JobQuoteItems, braceQuantity: number, fenceBraceId: number, fenceNote: string, fenceAreaNote: string) => {
        //calculate totals
        var itemId = quoteItem.itemId * 1;
        var chargeGst = itemChargeGst(itemId);
        var chargeTravel = false;
        let hirePeriod = quoteItem.customHirePeriod && quoteItem.invoicePeriodTypeId === 1 ? quoteItem.hirePeriod * 7 : quoteItem.hirePeriod;

        const apiUrl = 'CalculateQuotePrice?UseCustomPriceList=' + useCustomPriceList + '&JobId=' + id +
            '&CustomerId=' + customerId + "&ItemId=" + quoteItem.itemId + "&JobItemTypeId=" + quoteItem.typeId + "&ProductGroupId=" + quoteItem.productGroupId + "&HirePeriod=" + hirePeriod + "&UsePerMetrePricing=false" +
            "&FrequencyId=" + quoteItem.frequencyId + "&LocationId=" + quoteItem.locationId + "&Size=" + quoteItem.size + "&Quantity=" + quoteItem.quantity + "&ExtraHours=" + quoteItem.extraHours +
            "&ChargeGst=" + chargeGst + "&ExtraItemId=" + quoteItem.extras.itemId + "&ExtraItemQuantity=" + quoteItem.extras.quantity +
            "&IncludeTrolley=" + quoteItem.trolley + "&SiteAddressDuration=" + quoteDetails.siteAddressDuration + "&TruckId=" + quoteItem.truckId +
            "&IsEws=" + quoteItem.isEws + "&IsEvent=" + quoteItem.isEvent + "&ChargeTravel=" + chargeTravel + "&TotalSteps=0&IncludeStepsTravel=false&ChargeWeekendJob=false&ChargeEmergencyJob=false" +
            "&ChargeFuelSurcharge=" + chargeFuelSurcharge;


        try {
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                quoteItem.initialHire = result.data.initialHire;
                quoteItem.travelAmount = result.data.travelAmount;
                quoteItem.cleaningFee = result.data.cleaningAmount;
                quoteItem.trenchFee = result.data.trenchFeeAmount;
                quoteItem.fuelSurchargeAmount = result.data.fuelSurchargeAmount;
                quoteItem.rate = result.data.rate;
                quoteItem.gst = result.data.gst;
                quoteItem.amount = result.data.amount;
                quoteItem.overhireRate = result.data.overhireRate;

                setShowFenceBracing(false);
                setFenceArea(fenceAreaNote);
                setCalculatorIndex(-1);
                setCalculatorStatusId(0);
                setSaveDisabled(false);

                //create a new brace item
                var newIndex = quoteItems.length;
                if (braceQuantity > 0) {
                    createBraceItem(newIndex, quoteItems, fenceBraceId, braceQuantity, fenceNote);
                }
            }
        } catch (e) {
            setShowFenceBracing(false);
            setCalculatorIndex(-1);
            setCalculatorStatusId(0);
            setSaveDisabled(false);
            toast.error("Error getting calculating quote price!");
        }
    }

    const createBraceItem = (itemIndex: number, quoteItems: JobQuoteItems[], fenceBraceId: number, braceQty: number, braceNotes: string) => {
        var itemDetails = itemOptions.find(i => i.id === 145);
        if (itemDetails) {
            var newBraceItem: JobQuoteItems = {
                itemIndex: itemIndex - 1,
                id: 0,
                itemId: itemDetails.id,
                item: "Fencing Braces",
                itemDescription: "",
                installOnly: false,
                extendable: true,
                invoiceDescription: "",
                typeId: 51,
                productGroupId: itemDetails.productGroupId,
                steepRoofPitch: 0,
                isVoid: false,
                isTower: false,
                isWalkWay: false,
                unitOfMeasure: "each",
                status: "Quoted",
                statusId: 1,
                hirePeriod: itemDetails.priceListDefaultDays,
                defaultHirePeriod: itemDetails.priceListDefaultDays,
                customHirePeriod: false,
                hirePeriodWeeksDesc: "",
                frequencyId: -1,
                locationId: -1,
                fenceBraceId: fenceBraceId,
                size: 0,
                includeInstallDismantle: 0,
                quantity: braceQty,
                initialHire: 0,
                travelAmount: 0,
                cleaningFee: 0,
                trenchFee: 0,
                confinedSpaceAmount: 0,
                sampleTestingAmount: 0,
                wasteDisposalAmount: 0,
                fuelSurchargeAmount: 0,
                extraHours: 0,
                kwikstageInstalledForId: -1,
                rate: 0,
                gst: 0,
                amount: 0,
                overhireRate: 0,
                estCostPriceFormatted: "$0.00",
                estMarginFormatted: "$0.00",
                estMarginPercentageFormatted: "0%",
                callout: false,
                notes: braceNotes,
                quoteNote: "",
                jobQuoteDescription: "",
                jobQuoteInstalledFor: "",
                jobQuoteHeader: "",
                jobQuoteFloor: "",
                linkedId: 0,
                canEditTravel: true,
                itemOrder: itemIndex,
                showOnQuote: true,
                isEws: false,
                isEvent: false,
                truckId: -1,
                truckDoorsId: -1,
                trolley: false,
                extras: {
                    itemId: -1,
                    quantity: 0,
                    equipped: false
                },
                incStepsTravel: false,
                invoicePeriodTypeId: 2,
                tankLidExposed: false,
                weekendJob: false,
                emergencySameDayJob: false,
                epaQuestionsAnswered: false,
                isDirty: false
            };
            quoteItems.splice(itemIndex - 1, 0, newBraceItem);

            //update item indexs
            for (var i = 0; i < quoteItems.length; i++) {
                quoteItems[i].itemIndex = i;
            }

            //calculate the price for the braces
            calcQuotePrice(quoteItems, newBraceItem);
        }
    }
    //END FENCE BRACING

    const onCellCheckboxChange = (e: any, itemIndex: number) => {
        var quote = quoteDetails;
        var quoteItems = [...quote.jobQuoteItems];
        var quoteItem = quoteItems[itemIndex];
        quoteItem.showOnQuote = e.target.checked;
        setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
        setSaveDisabled(false);
        saveShowOnQuote(quoteItem.id, e.target.checked);
    }

    //Enter custom hire period
    const editCustomHirePeriod = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        var quoteItems = [...quote.jobQuoteItems];
        var item = quoteItems[itemIndex];
        item.customHirePeriod = true;
        item.hirePeriod = item.hirePeriod <= 0 ? 1 : item.invoicePeriodTypeId === 1 ? item.hirePeriod / 7 : item.hirePeriod;
        setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
        setSaveDisabled(false);
    }
    //END Custom Hire Period

    const saveShowOnQuote = async (quoteItemId: number, showOnQuote: boolean) => {
        try {
            var saveShowOnQuote = {
                jobQuoteItemId: quoteItemId,
                showOnQuote: showOnQuote
            };

            var jsonItem = JSON.stringify(saveShowOnQuote);

            const apiUrl = "SaveQuoteShowOnQuote";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                toast.success("Show on quote updated successfully");
            }
        } catch (e) {
            toast.error("Error saving job!");
        }     
    }

    const onCellPriceItemChange = (e: any, lastrow: boolean, itemIndex: number) => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[itemIndex];

        var value = e.target.value * 1;

        let newQuoteItem: any = {};
        newQuoteItem = quoteItem;
        newQuoteItem[e.target.name] = value;
        newQuoteItem.isDirty = true;

        //calculate amount field
        var itemAmount = quoteItem.initialHire + quoteItem.travelAmount + quoteItem.cleaningFee + quoteItem.trenchFee + quoteItem.confinedSpaceAmount + quoteItem.sampleTestingAmount + quoteItem.wasteDisposalAmount + quoteItem.fuelSurchargeAmount;
        quoteItem.amount = parseFloat(itemAmount.toFixed(2));
        //calculate rate & gst
        var rateGst = calculateRateGst(newQuoteItem.itemId, itemAmount);
        newQuoteItem.rate = rateGst.rate;
        newQuoteItem.gst = rateGst.gst;
        
        //check if this row is the last row, and if there is a value set and if so add a new blank row
        if (lastrow && (jobTypeId === 1 || jobTypeId === 3)) {
            let filteredItems = quoteDetails.jobQuoteItems.filter(data =>
                data.status != "Cancelled"
            );
            var blankLine = addBlankLine(quoteItems.length, filteredItems.length);
            quoteItems.push(blankLine);
        }
        var total = calculateTotal(quoteItems);
        var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        var totalInvoice = (total * 1) + (quoteDetails.totalInvoiceExtraAmount * 1);
        var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });

        setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: total, totalQuotedAmountFormatted: totalFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));
        setSaveDisabled(false);
    }

    const onHireCellChange = (lastrow: boolean, itemIndex: number, e: any) => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[itemIndex];

        let newQuoteItem: any = {};
        newQuoteItem = quoteItem;
        newQuoteItem[e.target.name] = e.target.value * 1;
        newQuoteItem.isDirty = true;

        //check if this row is the last row, and if there is a value set and if so add a new blank row
        if (lastrow && (jobTypeId === 1 || jobTypeId === 3)) {
            let filteredItems = quoteDetails.jobQuoteItems.filter(data =>
                data.status != "Cancelled"
            );
            var blankLine = addBlankLine(quoteItems.length, filteredItems.length);
            quoteItems.push(blankLine);
        }

        calcQuotePrice(quoteItems, quoteItem);  //this will update the state once it's calculated the new price
    }

    const bracingChange = (lastrow: boolean, itemIndex: number, value: number) => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[itemIndex];
        quoteItem.isDirty = true;
        quoteItem.fenceBraceId = value;

        //calculate the qty if we are bracing every panel / 2nd panel / 3rd panel
        if (value === 1 || value === 2 || value === 3) {
            //find the fencing item so we can get total fence metres
            var fenceItem = quoteItems.find(i => i.typeId === 50);
            if (fenceItem) {
                let fenceQty = fenceItem.quantity;
                var braceQty = fenceQty / 2.4 / value;
                braceQty = Math.trunc(braceQty);
                quoteItem.quantity = braceQty;
            }
        }

        //check if this row is the last row, and if there is a value set and if so add a new blank row
        if (lastrow && (jobTypeId === 1 || jobTypeId === 3)) {
            let filteredItems = quoteDetails.jobQuoteItems.filter(data =>
                data.status != "Cancelled"
            );
            var blankLine = addBlankLine(quoteItems.length, filteredItems.length);
            quoteItems.push(blankLine);
        }

        calcQuotePrice(quoteItems, quoteItem);
    }

    const onCellHoursChange = (e: any, lastrow: boolean, itemIndex: number) => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[itemIndex];
        var value = e.target.value * 1;

        let newQuoteItem: any = {};
        newQuoteItem = quoteItem;
        newQuoteItem[e.target.name] = value;
        newQuoteItem.isDirty = true;

        //check if this row is the last row, and if there is a value set and if so add a new blank row
        if (lastrow && (jobTypeId === 1 || jobTypeId === 3)) {
            let filteredItems = quoteDetails.jobQuoteItems.filter(data =>
                data.status != "Cancelled"
            );
            var blankLine = addBlankLine(quoteItems.length, filteredItems.length);
            quoteItems.push(blankLine);
        }

        if (quoteItem.typeId === 61) {
            newQuoteItem.trenchFee = value * epaTruckHireRate;
            calcEpaQuotePrice(quoteItem);
        } else {
            calcQuotePrice(quoteItems, quoteItem);
        }
    }

    const onCellItemChange = (e: any, lastrow: boolean, itemIndex: number) => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[itemIndex];
        let newQuoteItem: any = {};
        newQuoteItem = quoteItem;
        newQuoteItem.isDirty = true;
        let value = e.target.value * 1;
        newQuoteItem[e.target.name] = value;

        //if updating itemId then need to update defaults
        quoteItem.customHirePeriod = false;
        quoteItem.size = 0;
        quoteItem.extraHours = 0;
        quoteItem.initialHire = 0;
        quoteItem.travelAmount = 0;
        quoteItem.cleaningFee = 0;
        quoteItem.trenchFee = 0;
        quoteItem.confinedSpaceAmount = 0;
        quoteItem.sampleTestingAmount = 0;
        quoteItem.wasteDisposalAmount = 0;
        quoteItem.fuelSurchargeAmount = 0;
        quoteItem.amount = 0;
        quoteItem.overhireRate = 0;
        quoteItem.fenceBraceId = -1;
        quoteItem.frequencyId = -1;
        var itemRow = itemOptions.find(i => i.id === value);
        if (itemRow) {
            newQuoteItem["item"] = itemRow.name;
            if (itemRow.name.toLowerCase().includes("void")) {
                newQuoteItem["isVoid"] = true;
            } else {
                newQuoteItem["isVoid"] = false;
            }
            if (itemRow.name.toLowerCase().includes("tower")) {
                newQuoteItem["isTower"] = true;
                newQuoteItem["includeInstallDismantle"] = 1;
            } else {
                newQuoteItem["isTower"] = false;
                newQuoteItem["includeInstallDismantle"] = 0;
            }
            newQuoteItem["installOnly"] = itemRow.installOnly;
            newQuoteItem["extendable"] = itemRow.extendable;
            newQuoteItem["typeId"] = itemRow.typeId;
            newQuoteItem["unitOfMeasure"] = itemRow.unitOfMeasure;
            newQuoteItem["callout"] = itemRow.callout;
            newQuoteItem["hirePeriod"] = itemRow.priceListDefaultDays;
            newQuoteItem["defaultHirePeriod"] = itemRow.priceListDefaultDays;
            newQuoteItem["isWalkWay"] = itemRow.isWalkWay;
            newQuoteItem["isEws"] = itemRow.isEws;
            newQuoteItem["isEvent"] = itemRow.isEvent;
            newQuoteItem["truckId"] = itemRow.defaultTruckId;
            newQuoteItem["itemDescription"] = itemRow.description;
            newQuoteItem["invoicePeriodTypeId"] = itemRow.invoicePeriodTypeId;
            newQuoteItem["canEditTravel"] = true; //(itemRow.priceListItem && quote.siteAddressDuration > 0) || itemRow.id == 4 || itemRow.id == 25 || itemRow.id == 33 ? false : true;
            newQuoteItem["productGroupId"] = itemRow.productGroupId;

            //equipment is included in lunchroom pricing
            if (itemRow.typeId === 17) {
                quoteItem.extras.equipped = true;
            } else {
                quoteItem.extras.equipped = false;
            }

            if (itemRow.typeId === 29) {
                //showers automatically get a trolley added
                quoteItem.trolley = true;
            } else {
                quoteItem.trolley = false;
            }

            //make sure the extras are updated per item type
            if (itemRow.typeId === 14 && quoteItem.extras.quantity > 0) {
                quoteItem.extras.itemId = siteOfficeExtraId;
            } else if (itemRow.typeId === 17 && quoteItem.extras.quantity > 0) {
                quoteItem.extras.itemId = lunchRoomExtraId;
            } else if (itemRow.typeId != 27) {
                //lunchrooms and crossovers have extras
                //clear any extras as item id has changed to an item with no extras
                quoteItem.extras.itemId = -1;
                quoteItem.extras.quantity = 0;
            }

            if (systemCompanyId === 2 || itemRow.typeId === 8 || (itemRow.typeId >= 50 && itemRow.typeId <= 54)) {
                //Scaffold needs to be 0 as well
                //reset fencing items to 0 for qty
                quoteItem.quantity = 0;
            } else {
                quoteItem.quantity = 1;
            }

            if (itemRow.typeId === 58) {
                quoteItem.size = itemRow.name.includes("Treatment Plant") ? 7000 : 3200;
            } else if (itemRow.typeId === 59) {
                quoteItem.size = 1000;
            } else if (itemRow.typeId === 60) {
                quoteItem.size = 4000;
            }

            if (itemRow.typeId === 55) {
                let stepsItems = quoteItems.filter(i => i.typeId === 55 && i.status != "Cancelled");

                if (stepsItems.length === 1) {
                    //need to check if this is our first steps row and if so set inc steps travel to true
                    quoteItem.incStepsTravel = true;
                } else {
                    quoteItem.incStepsTravel = false;
                }
            } else {
                quoteItem.incStepsTravel = false;
            }

            if (itemRow.typeId === 61) {
                //default truck hours to 3 and calculate truck hire
                quoteItem.extraHours = 3;
                quoteItem.trenchFee = 3 * epaTruckHireRate;
            }
        }

        calculateQuotePrices(lastrow, itemIndex, "itemId", value, quoteItems, newQuoteItem);
    }

    const onCellChange = (lastrow: boolean, itemIndex: number, item: string, value: number) => {
        var quote = quoteDetails;
        var quoteItems = [...quote.jobQuoteItems];
        var quoteItem = quoteItems[itemIndex];
        let newQuoteItem: any = {};
        newQuoteItem = quoteItem;
        newQuoteItem.isDirty = true;
        newQuoteItem[item] = value;

        calculateQuotePrices(lastrow, itemIndex, item, value, quoteItems, newQuoteItem);
    }

    const calculateQuotePrices = (lastrow: boolean, itemIndex: number, item: string, value: number, quoteItems: JobQuoteItems[], quoteItem: JobQuoteItems) => {
        //check if this row is the last row, and if there is a value set and if so add a new blank row
        if (lastrow && (jobTypeId === 1 || jobTypeId === 3)) {
            let filteredItems = quoteDetails.jobQuoteItems.filter(data =>
                data.status != "Cancelled"
            );
            var blankLine = addBlankLine(quoteItems.length, filteredItems.length);
            quoteItems.push(blankLine);
        }

        //run different calculations depending on the item selected
        if (quoteItem.typeId === 61 && quoteItem.quantity > 0 && quoteItem.hirePeriod > 0) {
            calculateEpaPrice(quoteItems, quoteItem);
        } else if (quoteItem.isWalkWay && quoteItem.quantity > 0 && quoteItem.hirePeriod > 0) {
            calcWalkWayForRaise(itemIndex, quoteItems, quoteItem);
        } else if (quoteItem.isTower && quoteItem.size > 0 && quoteItem.quantity > 0 && quoteItem.hirePeriod > 0) {
            calcTower(quoteItems, quoteItem);
        } else if (item === "hirePeriod" && quoteItem.itemId === 4) {
            calcKwikstageExtend(quoteItem, value);
        } else if ((item === "hirePeriod" || item === "quantity") && (quoteItem.itemId === 25 || quoteItem.itemId === 33)) {
            setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
        } else if (quoteItem.quantity > 0 && quoteItem.hirePeriod > 0) {
            calcQuotePrice(quoteItems, quoteItem);
        } else {
            setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
        } 
    }

    const calculateEpaPrice = (quoteItems: JobQuoteItems[], quoteItem: JobQuoteItems) => {
        var amount = quoteItem.initialHire + quoteItem.travelAmount + quoteItem.cleaningFee + quoteItem.trenchFee + quoteItem.confinedSpaceAmount + quoteItem.sampleTestingAmount + quoteItem.wasteDisposalAmount + quoteItem.fuelSurchargeAmount;
        quoteItem.amount = parseFloat(amount.toFixed(2));;

        var total = calculateTotal(quoteItems);
        var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        var totalInvoice = (total * 1) + (quoteDetails.totalInvoiceExtraAmount * 1);
        var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });

        setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: total, totalQuotedAmountFormatted: totalFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));
        setSaveDisabled(false);
    }

    const calcWalkWayForRaise = async (itemIndex: number, quoteItems: JobQuoteItems[], quoteItem: JobQuoteItems) => {
        var itemId = quoteItem.itemId * 1;
        var chargeGst = itemChargeGst(itemId);

        var wwItem = {
            jobId: id,
            customerId: customerId,
            useCustomPriceList: useCustomPriceList,
            chargeGst: chargeGst,
            walkWayItem: quoteItem,
            quoteNo: quoteDetails.quoteNo,
            quoteNotes: quoteDetails.quoteNotes,
            manualQuoteNumber: quoteDetails.manualQuoteNumber,
            requireSiteVisit: quoteDetails.requireSiteVisit,
            siteVisitPerson: quoteDetails.siteVisitPerson,
            chargeFuelSurcharge: chargeFuelSurcharge,
        };

        try {
            var jsonItem = JSON.stringify(wwItem);

            const apiUrl = "CalculateWalkWayQuotePrice";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                let newquoteItem = result.data.walkWayItem;
                newquoteItem.itemIndex = itemIndex;
                quoteItems.splice(itemIndex, 1, newquoteItem);

                var wwRaise = result.data.walkWayRaiseItem;
                if (wwRaise) {
                    //check if wwRaise already exists and just needs updating
                    var existIndex = quoteItems.findIndex(jqi => jqi.id === wwRaise.id);
                    if (existIndex < 0) {
                        //add new raise
                        //insert the raise before the blank new row
                        var indexToInsert = quoteItems.length - 1;
                        wwRaise.itemIndex = indexToInsert;
                        indexToInsert = indexToInsert < 0 ? 0 : indexToInsert;
                        quoteItems.splice(indexToInsert, 0, wwRaise);

                        //update blank new row with next itemindex
                        var blankRowIndex = quoteItems.length - 1;
                        var blankRow = quoteItems[blankRowIndex];
                        if (blankRow) {
                            blankRow.itemIndex = (indexToInsert + 1);
                        }
                    } else {
                        //update existing raise
                        quoteItems.splice(existIndex, 1, wwRaise);
                    }
                }
                setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
                setSaveDisabled(false);
            }
        } catch (e) {
            toast.error("Error calculating walkway raise!");
        }     
    }

    const itemChargeGst = (itemId: number) => {
        var itemOption = itemOptions.find(i => i.id == itemId);
        var chargeGst = itemOption?.chargeGst;
        return chargeGst;
    }

    const calcTower = async (quoteItems: JobQuoteItems[], quoteItem: JobQuoteItems) => {
        try {
            var itemId = quoteItem.itemId * 1;
            var chargeGst = itemChargeGst(itemId);
            let incInsDis = quoteItem.includeInstallDismantle === 1;
            const apiUrl = 'CalculateTowerQuotePrice?JobId=' + id + '&ItemId=' + quoteItem.itemId + '&chargeGst=' + chargeGst + '&Size=' + quoteItem.size + '&ChargeInstall=' + incInsDis + '&Quantity=' + quoteItem.quantity + '&HirePeriod=' + quoteItem.hirePeriod + '&ChargeFuelSurcharge=' + chargeFuelSurcharge;

            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                quoteItem.travelAmount = result.data.travelAmount;
                quoteItem.fuelSurchargeAmount = result.data.fuelSurchargeAmount;
                quoteItem.rate = result.data.rate;
                quoteItem.gst = result.data.gst;
                quoteItem.amount = result.data.amount;
                quoteItem.initialHire = result.data.initialHire;
                quoteItem.overhireRate = result.data.overhireRate;

                var total = calculateTotal(quoteItems);
                var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                var totalInvoice = (total * 1) + (quoteDetails.totalInvoiceExtraAmount * 1);
                var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });

                setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: total, totalQuotedAmountFormatted: totalFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));
                setSaveDisabled(false);
            }
        } catch (e) {
            toast.error("Error getting tower price!");
        }
    }

    const calcKwikstageExtend = async (quoteItem: JobQuoteItems, hirePeriod: number) => {
        try {
            const apiUrl = 'GetKwikstageExtendQuotePrice?JobId=' + id + '&JobQuoteItemId=' + quoteItem.id + '&HirePeriod=' + hirePeriod;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var ksItem = result.data.kwikstageItemPrice;
                quoteItem.amount = ksItem.amount;
                quoteItem.rate = ksItem.rate;
                quoteItem.gst = ksItem.gst;

                var total = calculateTotal(quoteItems);
                var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                var totalInvoice = (total * 1) + (quoteDetails.totalInvoiceExtraAmount * 1);
                var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: total, totalQuotedAmountFormatted: totalFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));

                setSaveDisabled(false);
            }
        } catch (e) {
            toast.error("Error getting KS price!");
        }
    }

    const calcQuotePrice = async (quoteItems: JobQuoteItems[], quoteItem: JobQuoteItems) => {
        //calculate totals
        var itemId = quoteItem.itemId * 1;
        var chargeGst = itemChargeGst(itemId);

        var chargeTravel = quoteDetails.customerPickingUp < 1;       //default to true unless customer picking up is set to 1
        if (quoteItem.typeId === 27 || quoteItem.typeId === 28 || quoteItem.typeId === 40) {
            //crossovers and rubbish cages don't have a travel charge if their is a toilet being hired
            var hiringToilet = quoteItems.filter(i => (i.typeId === 25 || i.typeId === 33) && i.statusId != 8).length > 0;
            if (hiringToilet) {
                //toilet is being hired
                chargeTravel = false;
            }
        }

        let hirePeriod = quoteItem.customHirePeriod && quoteItem.invoicePeriodTypeId === 1 ? quoteItem.hirePeriod * 7 : quoteItem.hirePeriod;
        let totalSteps = 0;
        let stepItems = quoteItems.filter(i => i.typeId === 55 && i.status != "Cancelled");
        if (quoteItem.typeId === 55) {
            //need to sum up all the steps quantity to calculate the travel
            for (var i = 0; i < stepItems.length; i++) {
                totalSteps += stepItems[i].quantity;
            }
        }

        try {
            const apiUrl = 'CalculateQuotePrice?UseCustomPriceList=' + useCustomPriceList + '&JobId=' + id +
                '&CustomerId=' + customerId + "&ItemId=" + quoteItem.itemId + "&JobItemTypeId=" + quoteItem.typeId + "&ProductGroupId=" + quoteItem.productGroupId + "&HirePeriod=" + hirePeriod + "&UsePerMetrePricing=false" +
                "&FrequencyId=" + quoteItem.frequencyId + "&LocationId=" + quoteItem.locationId + "&Size=" + quoteItem.size + "&Quantity=" + quoteItem.quantity + "&ExtraHours=" + quoteItem.extraHours +
                "&ChargeGst=" + chargeGst + "&ExtraItemId=" + quoteItem.extras.itemId + "&ExtraItemQuantity=" + quoteItem.extras.quantity +
                "&IncludeTrolley=" + quoteItem.trolley + "&SiteAddressDuration=" + quoteDetails.siteAddressDuration + "&TruckId=" + quoteItem.truckId +
                "&IsEws=" + quoteItem.isEws + "&IsEvent=" + quoteItem.isEvent + "&ChargeTravel=" + chargeTravel + "&TotalSteps=" + totalSteps + "&IncludeStepsTravel=" + quoteItem.incStepsTravel +
                "&ChargeWeekendJob=" + quoteItem.weekendJob + "&ChargeEmergencyJob=" + quoteItem.emergencySameDayJob + "&ChargeFuelSurcharge=" + chargeFuelSurcharge;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                quoteItem.initialHire = result.data.initialHire;
                quoteItem.travelAmount = result.data.travelAmount;
                quoteItem.cleaningFee = result.data.cleaningAmount;
                quoteItem.trenchFee = result.data.trenchFeeAmount;
                quoteItem.fuelSurchargeAmount = result.data.fuelSurchargeAmount;
                quoteItem.rate = result.data.rate;
                quoteItem.gst = result.data.gst;
                quoteItem.amount = result.data.amount;
                quoteItem.overhireRate = result.data.overhireRate;

                if (quoteItem.typeId === 55 && !quoteItem.incStepsTravel) {
                    //for steps we store the travel amount against the first steps item
                    var stepItem = stepItems.find(si => si.incStepsTravel);
                    if (stepItem) {
                        stepItem.travelAmount = result.data.stepsTravelAmount;
                        stepItem.amount = stepItem.initialHire + stepItem.travelAmount;
                    }
                }

                var total = calculateTotal(quoteItems);
                var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                var totalInvoice = (total * 1) + (quoteDetails.totalInvoiceExtraAmount * 1);
                var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: total, totalQuotedAmountFormatted: totalFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));

                setSaveDisabled(false);
            }
        } catch (e) {
            toast.error("Error getting quote price!");
        }
    }

    const calcEpaQuotePrice = (quoteItem: JobQuoteItems) => {
        var amount = quoteItem.initialHire + quoteItem.trenchFee + quoteItem.confinedSpaceAmount + quoteItem.sampleTestingAmount + quoteItem.wasteDisposalAmount + quoteItem.travelAmount + quoteItem.cleaningFee + quoteItem.fuelSurchargeAmount;
        quoteItem.amount = parseFloat(amount.toFixed(2));;

        var total = calculateTotal(quoteItems);
        var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        var totalInvoice = (total * 1) + (quoteDetails.totalInvoiceExtraAmount * 1);
        var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: total, totalQuotedAmountFormatted: totalFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));

        setSaveDisabled(false);
    }

    const calculateTotal = (quoteItems: JobQuoteItems[]) => {
        var quoteTotal = 0;
        for (var i = 0; i < quoteItems.length - 1; i++) {
            if (quoteItems[i].statusId != 8) {
                //only include non cancelled items
                quoteTotal += (quoteItems[i].amount * 1);
                
            }
        }
        
        return quoteTotal;
    }

    const onCellChangeNumber = (lastrow: boolean, itemIndex: number, item: string, value: any) => {
        var quoteItems = [...quoteDetails.jobQuoteItems]; 
        var quoteItem = quoteItems[itemIndex];
        let newQuoteItem: any = {};
        newQuoteItem = quoteItem;
        newQuoteItem[item] = value * 1;

        if (item == "amount") {
            //calc rate and gst
            var amount = value * 1;
            var rateGst = calculateRateGst(newQuoteItem.itemId, amount);
            newQuoteItem.rate = rateGst.rate;
            newQuoteItem.gst = rateGst.gst;
        }

        //check if this row is the last row, and if there is a value set and if so add a new blank row
        if (lastrow && (jobTypeId === 1 || jobTypeId === 3)) {
            let filteredItems = quoteDetails.jobQuoteItems.filter(data =>
                data.status != "Cancelled"
            );
            var blankLine = addBlankLine(quoteItems.length, filteredItems.length);
            quoteItems.push(blankLine);
        }

        //recalc total
        var total = calculateTotal(quoteItems);
        var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        var totalInvoice = (total * 1) + (quoteDetails.totalInvoiceExtraAmount * 1);
        var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
        setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: total, totalQuotedAmountFormatted: totalFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));
        setSaveDisabled(false);
    }

    const calculateRateGst = (itemId: number, amount: number) => {
        var itemRow = itemOptions.find(i => i.id === itemId);
        var chargeGst = true;
        if (itemRow) {
            chargeGst = itemRow.chargeGst;
        }

        if (chargeGst) {
            //calculate rate and GST
            var gstToCalc = gst + 1;
            var rate = amount / gstToCalc;
            var gstAmount = amount - rate;
            var rateGst: RateGst = {
                rate: rate,
                gst: gstAmount
            };
            return rateGst;
        }

        var rateGst: RateGst = {
            rate: amount,
            gst: 0
        };
        return rateGst;
    }

    //Cancel Popup
    const cancelQuoteItem = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        var noItemsMin = jobTypeId === 1 || jobTypeId === 3 ? 1 : 0;
        if (quote.jobQuoteItems.length > noItemsMin) {
            getCancelReasons(itemIndex);
        } else {
            //can't delete only line
            toast.error("Cannot delete new row");
        }
    }

    const getCancelReasons = async (itemIndex: number) => {
        var quote = quoteDetails;
        var itemId = quote.jobQuoteItems[itemIndex].itemId;
        //look up item in item list
        var itemOptionsIndex = itemOptions.findIndex(items => items.id === itemId);
        var item = itemOptions[itemOptionsIndex].name;

        try {
            const apiUrl = "GetCancelReasons";
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                //show popup to cancel
                setShowCancelModal(true);
                setCancelItem(item);
                setCancelItemIndexId(itemIndex);
                setCancelReasonId(-1);
                setCancelOptions(result.data);
            }
        } catch (e) {
            toast.error("Error getting cancel reasons!");
        }
    }

    const hideCancelModal = () => {
        setShowCancelModal(false);
        setCancelItem("");
        setCancelItemIndexId(-1);
        setCancelReasonId(-1);
        setSaveDisabled(false);
    }

    const handleCancelReasonChange = (e: any) => {
        let errorsList: any = {};

        if (e.target.value < 0) {
            errorsList["cancelReason"] = "Valid Cancel Reason Required";
            setErrors(errorsList);
        } else {
            setErrors({});
            setCancelReasonId(Number(e.target.value));
        }
    }

    const validateCancel = () => {
        let errorsList: any = {};

        if (cancelReasonId < 0) {
            errorsList["cancelReason"] = "Cancel Reason is required";
            setErrors(errorsList);
            return false;
        } else {
            return true;
        }
    }

    const saveCancel = () => {
        if (validateCancel()) {
            saveCancelQuote();
        }
    }

    const saveCancelQuote = async () => {
        //cancel the item 
        var quote = quoteDetails;
        var quoteItems = quote.jobQuoteItems;
        var quoteItem = quoteItems[cancelItemIndexId];
        if (quoteItem) {
            quoteItem.status = "Cancelled";
            quoteItem.statusId = 8;
            //make sure user hasn't cleared any fields before cancelling item
            quoteItem.hirePeriod = !quoteItem.hirePeriod ? 0 : quoteItem.hirePeriod;
            quoteItem.quantity = !quoteItem.quantity ? 0 : quoteItem.quantity;
            quoteItem.amount = !quoteItem.amount ? 0 : quoteItem.amount;
            quoteItem.overhireRate = !quoteItem.overhireRate ? 0 : quoteItem.overhireRate;
            if (quoteItem.id > 0) {
                //update database
                var cancelItem = {
                    jobQuoteItemId: quoteItem.id,
                    jobId: quoteDetails.jobId,
                    cancelReasonId: cancelReasonId
                };

                try {
                    var jsonItem = JSON.stringify(cancelItem);

                    const apiUrl = "CancelQuoteItem";
                    var result = await PostQuery(apiUrl, jsonItem);
                    if (result.error) {
                        toast.error("Error saving data!");
                    } else {
                        if (result.data == true) {
                            toast.success("Item Cancelled");

                            //recalc total
                            var totalQuotedAmount = calculateTotal(quote.jobQuoteItems);
                            quote.totalQuotedAmount = totalQuotedAmount;
                            quote.totalQuotedAmountFormatted = totalQuotedAmount.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                            quote.totalInvoiceAmount = (totalQuotedAmount * 1) + (quote.totalInvoiceExtraAmount * 1);
                            quote.totalInvoiceAmountFormatted = quote.totalInvoiceAmount.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });

                            let itemOrder = 1;
                            for (var i = 0; i < quoteItems.length; i++) {
                                if (quoteItems[i].status != "Cancelled") {
                                    quoteItems[i].itemOrder = itemOrder;
                                    itemOrder += 1;
                                }
                            }

                            if (quoteItem.typeId === 55) {
                                //deleting steps so may need to recalc travel if there are other steps items in the quote
                                checkStepTravel(quoteItems);
                            } else {
                                setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
                                setSaveDisabled(false);
                                setShowCancelModal(false);
                                setCancelItem("");
                                setCancelItemIndexId(-1);
                                setCancelReasonId(-1);
                            }
                        } else {
                            toast.error("There was an error cancelling the item, please refresh and try again!");
                        }
                    }
                } catch (e) {
                    toast.error("Error cancelling item!");
                }
            } else {
                let itemOrder = 1;
                for (var i = 0; i < quoteItems.length; i++) {
                    if (quoteItems[i].status != "Cancelled") {
                        quoteItems[i].itemOrder = itemOrder;
                        itemOrder += 1;
                    }
                }

                if (quoteItem.typeId === 55) {
                    checkStepTravel(quoteItems);
                } else {
                    setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
                    setSaveDisabled(false);
                    setShowCancelModal(false);
                    setCancelItem("");
                    setCancelItemIndexId(-1);
                    setCancelReasonId(-1);
                }
            }
        }
    }

    //steps may need the price re-calc if deleted
    const checkStepTravel = (quoteItems: JobQuoteItems[]) => {
        //deleting steps so may need to recalc travel if there are other steps items in the quote
        let stepItems = quoteItems.filter(i => i.typeId === 55 && i.status != "Cancelled");
        if (stepItems.length > 0) {
            let stepItem = stepItems.find(i => i.incStepsTravel);
            if (!stepItem) {
                stepItem = stepItems[0];
                stepItem.incStepsTravel = true;
            };

            setSaveDisabled(false);
            setShowCancelModal(false);
            setCancelItem("");
            setCancelItemIndexId(-1);
            setCancelReasonId(-1);

            //need to re-calc the step item to include travel
            calcQuotePrice(quoteItems, stepItem);
        } else {
            setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
            setSaveDisabled(false);
            setShowCancelModal(false);
            setCancelItem("");
            setCancelItemIndexId(-1);
            setCancelReasonId(-1);
        }
    }
    //End Cancel Popup

    const createQuote = () => {
        if (saveDisabled) {
            return;
        }
        setSaveDisabled(true);
        createNewQuote();
    }

    const createNewQuote = async () => {
        try {
            var quoteToCreate = {
                jobId: id
            };

            var jsonItem = JSON.stringify(quoteToCreate);

            const apiUrl = "CreateQuote";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                toast.success("Quote Saved");
                var blankLine = addBlankLine(0, 0);
                //then need to clear notes so can resave and add note to list
                var quote: JobQuoteDetails = {
                    id: result.data.id,
                    jobId: result.data.jobId,
                    quoteNo: result.data.quoteNo,
                    manualQuoteNumber: result.data.manualQuoteNumber,
                    isEvent: result.data.isEvent,
                    requireSiteVisit: result.data.requireSiteVisit,
                    siteVisitPerson: -1,
                    siteContactName: result.data.siteContactName,
                    siteContactPhone: result.data.siteContactPhone,
                    showSiteVisitNote: true,
                    totalQuotedAmount: 0,
                    totalQuotedAmountFormatted: "$0.00",
                    totalInvoiceExtraAmount: 0,
                    totalInvoiceExtraAmountFormatted: "$0.00",
                    totalInvoiceAmount: 0,
                    totalInvoiceAmountFormatted: "$0.00",
                    jobQuoteItems: [blankLine],
                    quoteNotes: result.data.quoteNotes,
                    siteAddressDuration: result.data.siteAddressDuration,
                    customerPickingUp: 0,
                    isDirty: false
                };
                setQuoteDetails(quote);
                setSaveDisabled(false);
            }
        } catch (e) {
            toast.error("Error creating quote!");
        }
    }

    //Calculate Kwikstage Popup 
    const calculateKwikstage = (e: any, itemIndex: number) => {
        e.preventDefault();

        calcKwikstage(itemIndex);
    }

    const calcKwikstage = async (itemIndex: number) => {
        try {
            var quoteItems = quoteDetails.jobQuoteItems;
            var quoteItem = quoteItems[itemIndex];

            const apiUrl = 'GetKwikstageCalculatorItems?JobQuoteItemId=' + quoteItem.id;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                var items = result.data;
                var blankRow = addBlankCalculatorRow();
                items.push(blankRow);

                var kwikstageItem: KwikstageItem = {
                    extraHours: quoteItem.extraHours,
                    kwikstageInstalledFor: quoteItem.kwikstageInstalledForId,
                    quoteNote: quoteItem.quoteNote
                };

                setShowCalculator(true);
                setCalculatorIndex(itemIndex);
                setCalculatorItems(items);
                setCalculatorKwikstageItem(kwikstageItem);
                setCalculatorStatusId(quoteItem.statusId);
            }
        } catch (e) {
            toast.error("Error getting getting KS items!");
        }
    }

    const addBlankCalculatorRow = () => {
        var item: KwikstageCalculation = {
            id: 0,
            starterBayId: -1,
            liftId: -1,
            bays: 0,
            baysRemaining: 0
        };
        return item;
    }

    const resetKwikstageItem = () => {
        var item: KwikstageItem = {
            extraHours: 0,
            kwikstageInstalledFor: -1,
            quoteNote: ""
        };

        return item;
    }

    const hideCalculateKwikstage = () => {
        var kwikstageItem = resetKwikstageItem();
        setShowCalculator(false);
        setCalculatorIndex(-1);
        setCalculatorItems([]);
        setCalculatorKwikstageItem(kwikstageItem);
        setCalculatorStatusId(0);
    }

    const updateCalculateKwikstage = (items: KwikstageCalculation[]) => {
        setCalculatorItems(items);
    }

    const updateCalculatorKwikstageItem = (item: KwikstageItem) => {
        setCalculatorKwikstageItem(prevState => ({ ...prevState, item }));
    }

    const saveCalculateKwikstage = () => {
        calculateKwikstagePrice();
    }

    const calculateKwikstagePrice = async () => {
        var quote = quoteDetails;
        var quoteItems = [...quote.jobQuoteItems];
        var quoteItem = quoteItems[calculatorIndex];

        var calcKwikstageItem = calculatorKwikstageItem;
        var calulatorItems = calculatorItems;
        calulatorItems.pop();

        var calculateKwikstagePrice = {
            id: quoteItem.id,
            jobQuoteId: quote.quoteNo,
            jobId: quote.jobId,
            itemId: quoteItem.itemId,
            hirePeriod: quoteItem.hirePeriod,
            extraHours: calcKwikstageItem.extraHours,
            kwikstageInstalledForId: calcKwikstageItem.kwikstageInstalledFor,
            quoteNote: calcKwikstageItem.quoteNote,
            itemOrder: quoteItem.itemOrder,
            calculatorItems: calulatorItems
        }

        try {
            var jsonItem = JSON.stringify(calculateKwikstagePrice);

            const apiUrl = "CalculateKwikstageQuotePrice";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                var newquoteItem = result.data.kwikstageItem;
                newquoteItem.itemIndex = calculatorIndex;
                quoteItems.splice(calculatorIndex, 1, newquoteItem);
                var kwikstageItem = resetKwikstageItem();
                var kwikstageAdjustmentItem = result.data.kwikstageAdjustmentItem;

                if (kwikstageAdjustmentItem) {
                    var existItemIndex = quoteItems.findIndex(qi => qi.id === kwikstageAdjustmentItem.id);
                    if (existItemIndex >= 0) {
                        quoteItems.splice(existItemIndex, 1, result.data.kwikstageAdjustmentItem);
                    } else {
                        //add the adjustment after the kwikstage item
                        var indexToAdd = calculatorIndex + 1;
                        quoteItems.splice(indexToAdd, 0, result.data.kwikstageAdjustmentItem);
                    }
                }

                //reset item index
                let itemOrder = 1;
                for (var i = 0; i < quoteItems.length; i++) {
                    quoteItems[i].itemIndex = i;
                    if (quoteItems[i].status != "Cancelled") {
                        quoteItems[i].itemOrder = itemOrder;
                        itemOrder += 1;
                    }
                }

                var totalQuotedAmount = calculateTotal(quote.jobQuoteItems);
                //quote.totalQuotedAmount = total;
                var totalQuotedAmountFormatted = totalQuotedAmount.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
                var totalInvoiceAmount = (totalQuotedAmount * 1) + (quote.totalInvoiceExtraAmount * 1);
                var totalInvoiceAmountFormatted = quote.totalInvoiceAmount.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });

                setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: totalQuotedAmount, totalQuotedAmountFormatted: totalQuotedAmountFormatted, totalInvoiceAmount: totalInvoiceAmount, totalInvoiceAmountFormatted: totalInvoiceAmountFormatted }));
                setSaveDisabled(false);
                setShowCalculator(false);
                setCalculatorIndex(-1);
                setCalculatorItems([]);
                setCalculatorKwikstageItem(kwikstageItem);
                setCalculatorStatusId(0);

                toast.success("Kwikstage Item has been saved");
            }
        } catch (e) {
            toast.error("Error calculating KS price!");
        }
    }
    //end calculate kwikstage

    //Quote Site Notes Popup
    const addSiteNotes = () => {
        if (quoteDetails.id === 0) {
            toast.error("You must save the quote before adding site notes!");
        } else {
            getSiteNotes();
        }
    }

    const getSiteNotes = async () => {
        try {
            const apiUrl = 'GetJobQuoteSiteNotes?JobQuoteId=' + quoteDetails.id;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                setShowAddSiteNotes(true);
                setSiteNotes(result.data);
            }
        } catch (e) {
            toast.error("Error getting site notes!");
        }
    }

    const hideSiteNotes = () => {
        setShowAddSiteNotes(false);
        setSiteNotes([]);
    }

    const updateSiteNotes = (updatedSiteNotes: GenericList[]) => {
        setSiteNotes(updatedSiteNotes);
    }
    //End Quote Site Notes Popup

    //Quote Note Popup
    const addQuoteNotes = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quoteItems = quoteDetails.jobQuoteItems;
        var quoteItem = quoteItems[itemIndex];
        var quoteNote = quoteItem.quoteNote;
        setShowQuoteNote(true);
        setQuoteNoteIndex(itemIndex);
        setQuoteNote(quoteNote);
    }

    const hideQuoteNote = () => {
        setShowQuoteNote(false);
        setQuoteNote("");
        setQuoteNoteIndex(-1);
    }

    const updateQuoteNote = (note: string) => {
        setQuoteNote(note);
    }

    const saveQuoteNote = () => {
        var note = quoteNote;

        if (quoteNoteIndex >= 0) {
            saveQuoteNotes(quoteNoteIndex, note, 1);
        } else {
            setShowQuoteNote(false);
            setQuoteNote("");
            setQuoteNoteIndex(-1);
        }
    }
    //End Quote Note Popup

    //Notes Popup 
    const editItemNotes = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        if (quote.jobQuoteItems.length > 0) {
            var quoteItem = quote.jobQuoteItems[itemIndex];

            var editNoteItem: EditNoteItem = {
                heading: "",
                parentIndex: 0,
                rowIndex: itemIndex,
                id: quoteItem.id,
                itemId: quoteItem.itemId,
                item: quoteItem.item,
                notes: quoteItem.notes
            }

            setEditNotes(editNoteItem);
            setShowEditNotesModal(true);
        }
  
    }

    const hideNotes = () => {
        var editNoteItem: EditNoteItem = {
            heading: "",
            parentIndex: 0,
            rowIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            notes: ""
        }

        setEditNotes(editNoteItem);
        setShowEditNotesModal(false);
    }

    const updateNotes = (updatedNotes: EditNoteItem) => {
        setEditNotes(prevState => ({ ...prevState, notes: updatedNotes.notes }));
    }

    const saveNotes = () => {
        //only save at this point if the quote item has been saved, otherwise wait until the quote item is saved
        if (editNotes.id > 0) {
            saveQuoteNotes(editNotes.rowIndex, editNotes.notes, 2);
        } else {
            //reset
            var editNoteItem: EditNoteItem = {
                heading: "",
                parentIndex: 0,
                rowIndex: 0,
                id: 0,
                itemId: 0,
                item: "",
                notes: ""
            }
            setEditNotes(editNoteItem);
            setShowEditNotesModal(false);
        }
    }

    const saveQuoteNotes = async (itemIndex: number, updatedNotes: string, noteType: number) => {
        //noteType => 1 for Quote Notes and 2 for Notes
        //save notes
        var quote = quoteDetails;
        var quoteItems = [...quote.jobQuoteItems];
        var quoteItem = quoteItems[itemIndex];
        if (quoteItem) {
            var existQuoteNotes = quoteItem.quoteNote;
            var existNotes = quoteItem.notes;

            var quoteItemNotesSave: QuoteItemNotes = {
                id: quoteItem.id,
                jobId: quote.jobId,
                itemId: quoteItem.itemId,
                notes: noteType === 2 ? updatedNotes : existNotes,
                quoteNote: noteType === 1 ? updatedNotes : existQuoteNotes
            };

            try {
                var jsonItem = JSON.stringify(quoteItemNotesSave);

                const apiUrl = "SaveQuoteItemNotes";
                var result = await PostQuery(apiUrl, jsonItem);
                if (result.error) {
                    toast.error("Error saving data!");
                } else {
                    toast.success("Notes Saved");

                    //reset
                    var editNoteItem: EditNoteItem = {
                        heading: "",
                        parentIndex: 0,
                        rowIndex: 0,
                        id: 0,
                        itemId: 0,
                        item: "",
                        notes: ""
                    }
                    quoteItem.quoteNote = noteType === 1 ? updatedNotes : existQuoteNotes;
                    quoteItem.notes = result.data.notes;     //update the notes as I am adding the quote notes to the notes field

                    setQuoteDetails(quote);
                    setEditNotes(editNoteItem);
                    setShowEditNotesModal(false);
                    setShowQuoteNote(false);
                    setQuoteNote("");
                    setQuoteNoteIndex(-1);
                }
            } catch (e) {
                toast.error("Error saving quote notes!");
            }
        }
    }
    //End Notes Popup

    //Extras - Dunny Only
    //EXTRA ITEMS
    const addExtraItems = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        var quoteItem = quote.jobQuoteItems[itemIndex];
        let extras: NewJobQuoteItemExtra =
        {
            itemId: quoteItem.typeId === 14 ? siteOfficeExtraId : quoteItem.typeId === 17 ? lunchRoomExtraId : 0,
            equipped: quoteItem.extras.equipped,
            quantity: quoteItem.extras.quantity
        };

        //quoteItem.extras;
        let isItemLarge = quoteItem.item.includes("6.0");
        let noTables = isItemLarge ? 2 : 1;
        let noChairs = quoteItem.typeId === 14 && isItemLarge ? 4 : quoteItem.typeId === 17 && isItemLarge ? 8 : quoteItem.typeId === 17 ? 4 :  2;

        var canEdit = quoteItem.statusId == 1 || quoteItem.statusId == 2;

        setShowExtraItems(true);
        setEditExtraItems(canEdit);
        setExtraIndex(itemIndex);
        setItemDescription(quoteItem.itemDescription);
        setTables(noTables);
        setChairs(noChairs);
        setExtras(extras);
    }

    const hideExtraItems = () => {
        var extra: NewJobQuoteItemExtra = {
            itemId: -1,
            quantity: 0,
            equipped: false
        };
        setShowExtraItems(false);
        setEditExtraItems(false);
        setExtraIndex(-1);
        setItemDescription("");
        setTables(1);
        setChairs(2);
        setExtras(extra);
    }

    const updateExtraItems = (updatedExtraItems: NewJobQuoteItemExtra) => {
        setExtras(prevState => ({ ...prevState, itemId: updatedExtraItems.itemId, equipped: updatedExtraItems.equipped, quantity: updatedExtraItems.quantity }));
    }

    //delivery address isn't used here as it only gets set when the item is scheduled
    const saveExtraItems = (_deliveryAddress: DeliveryAddress) => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[extraIndex];

        //update quote
        quoteItem.extras.itemId = extras.itemId;
        quoteItem.extras.quantity = extras.quantity;
        quoteItem.extras.equipped = extras.equipped;

        //reset as cannot save from this screen - shouldn't be called
        var extra: NewJobQuoteItemExtra = {
            itemId: -1,
            quantity: 0,
            equipped: false
        };

        setShowExtraItems(false);
        setEditExtraItems(false);
        setExtraIndex(-1);
        setItemDescription("");
        setTables(1);
        setChairs(2);
        setExtras(extra);
        calcQuotePrice(quoteItems, quoteItem);
    }
    //END EXTRA ITEMS

    //EXTRA CROSSOVER SECTIONS
    const calculateCrossoverQuantity = (e: any, itemIndex: number) => {
        e.preventDefault();
        var quote = quoteDetails;
        var quoteItem = quote.jobQuoteItems[itemIndex];
        var quoteItemExtra = quoteItem.extras;

        //calculate total length
        var extraQty = quoteItemExtra ? quoteItemExtra.quantity : 0;
        var totalCrossoverLength = (quoteItem.quantity * 4.8) + (extraQty * 0.3);

        var extraCrossoverSection: JobQuoteExtraSection = {
            totalLength: totalCrossoverLength,
            crossoverQuantity: quoteItem.quantity,
            sectionQuantity: extraQty,
            totalCrossoverLength: totalCrossoverLength.toFixed(1)
        };

        var canEdit = quoteItem.statusId == 1 || quoteItem.statusId == 2;

        setShowExtraSection(true);
        setExtraIndex(itemIndex);
        setExtraSection(extraCrossoverSection);
        setEditExtraItems(canEdit);
    }

    const updateExtraSection = (extraCrossoverSection: JobQuoteExtraSection) => {
        var totalCrossoverLength = (extraCrossoverSection.crossoverQuantity * 4.8) + (extraCrossoverSection.sectionQuantity * 0.3);
        extraCrossoverSection.totalCrossoverLength = totalCrossoverLength.toFixed(1);

        setExtraSection(extraCrossoverSection);
    }

    const hideExtraSection = () => {
        var extraCrossoverSection: JobQuoteExtraSection = {
            totalLength: 0,
            crossoverQuantity: 0,
            sectionQuantity: 0,
            totalCrossoverLength: "0.0"
        };

        setShowExtraSection(false);
        setExtraIndex(-1);
        setExtraSection(extraCrossoverSection);
        setEditExtraItems(false);
    }

    const saveCrossoverTotal = () => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[extraIndex];

        //update quote
        quoteItem.extras.itemId = quoteItem.itemId;
        quoteItem.extras.quantity = extraSection.sectionQuantity;
        quoteItem.quantity = extraSection.crossoverQuantity;

        //reset
        var extraCrossoverSection: JobQuoteExtraSection = {
            totalLength: 0,
            crossoverQuantity: 0,
            sectionQuantity: 0,
            totalCrossoverLength: "0.0"
        };

        setShowExtraSection(false);
        setExtraIndex(-1);
        setExtraSection(extraCrossoverSection);
        setEditExtraItems(false);

        calcQuotePrice(quoteItems, quoteItem);
    }
    //END EXTRA CROSSOVER SECTIONS

    //INVOICE DESCRIPTION
    const editInvoiceDescription = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        var quoteItem = quote.jobQuoteItems[itemIndex];

        var readonly = (quoteItem.statusId == 1 || quoteItem.statusId == 2) ? false : true;

        setShowInvoiceDescription(true);
        setEditTrolleyReadOnly(readonly);
        setExtraIndex(itemIndex);
        setJobQuoteItemId(quoteItem.id);
        setExistInvoiceDescription(quoteItem.invoiceDescription);
        setTrolley(quoteItem.trolley);
    }

    const hideInvoiceDescription = () => {
        setShowInvoiceDescription(false);
        setExtraIndex(-1);
        setJobQuoteItemId(0);
        setExistInvoiceDescription("");
        setTrolley(false);
    }

    const updateDeliveryAddress = (_updatedDelAddress: DeliveryAddress) => {
        //not used
    }

    const clearDeliveryAddress = () => {
        //not used
    }

    const updateInvoiceDescription = (updatedInvDesc: string) => {
        setExistInvoiceDescription(updatedInvDesc);
    }

    const updateTrolley = (updatedTrolley: boolean) => {
        setTrolley(updatedTrolley);
    }

    //Delivery Address can't be updated here, has to wait for the item to be scheduled
    const saveInvoiceDescription = () => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[extraIndex];

        quoteItem.invoiceDescription = existInvoiceDescription;
        quoteItem.trolley = trolley;
        
        setShowInvoiceDescription(false);
        setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems }));
        setExtraIndex(-1);
        setJobQuoteItemId(-1);
        setExistInvoiceDescription("");
        setTrolley(false);
        calcQuotePrice(quoteItems, quoteItem);
    }
    //END INVOICE DESCRIPTION

    //SCHEDULE ON HOLD JOB
    const scheduleOnHoldJob = (e: any, itemIndex: number, onHoldJob: boolean) => {
        e.preventDefault();

        scheduleOnHoldQuotedJob(itemIndex, onHoldJob);
    }

    const scheduleOnHoldQuotedJob = (itemIndex: number, onHoldJob: boolean) => {
        var quote = quoteDetails;
        var quoteItem = quote.jobQuoteItems[itemIndex];
        if (quoteItem.isDirty || quote.isDirty) {
            //save the job
            saveQuote("schedule", onHoldJob, itemIndex);
        } else {
            schedOnHoldQuotedJob(quote, quoteItem, onHoldJob);
        }
    }

    const schedOnHoldQuotedJob = async (quote: JobQuoteDetails, quoteItem: JobQuoteItems, onHoldJob: boolean) => {
        if (await validateScheduleJob(quote, quoteItem)) {
            var schItem: ScheduleItemDetail = {
                jobId: Number(id),
                jobQuoteItemId: quoteItem.id,
                item: quoteItem.item,
                itemTypeId: quoteItem.typeId,
                installOnly: quoteItem.installOnly,
                isEvent: quoteItem.isEvent || quoteDetails.isEvent,
                needsPickup: scheduleJobNeedsPickup || quoteDetails.isEvent,
                hirePeriod: quoteItem.hirePeriod,
                frequencyId: quoteItem.frequencyId,
                customerAccountTypeId: customerAccountTypeId,
                customerLinkedToReckon: customerLinkedToReckon,
                siteContact: (quote.siteContactName ?? "") + " " + (quote.siteContactPhone ?? ""),
                jobOnHold: onHoldJob
            };

            setShowScheduleJob(true);
            setScheduleItemDetail(schItem);
        } else {
            toast.error("Please fix validation errors before scheduling item!");
        }
    }

    const resetScheduleItemDetail = () => {
        var schItem: ScheduleItemDetail = {
            jobId: Number(id),
            jobQuoteItemId: 0,
            item: "",
            itemTypeId: -1,
            installOnly: false,
            isEvent: false,
            needsPickup: scheduleJobNeedsPickup,
            hirePeriod: -1,
            frequencyId: -1,
            customerAccountTypeId: customerAccountTypeId,
            customerLinkedToReckon: customerLinkedToReckon,
            siteContact: "",
            jobOnHold: false
        };

        return schItem;
    }

    const hideScheduleOnHoldJob = () => {
        setShowScheduleJob(false);
        var resetSchItem = resetScheduleItemDetail();

        setScheduleItemDetail(resetSchItem);
    }

    const saveScheduleOnHoldJob = () => {
        setShowScheduleJob(false);

        var resetSchItem = resetScheduleItemDetail();
        setScheduleItemDetail(resetSchItem);

        getQuote();
    }

    const validateScheduleJob = async (quote: JobQuoteDetails, quoteItem: JobQuoteItems) => {
        //need to make sure we have a site contact name and phone number, doors facing selected (for certain items)
        let errorList: any = {};
        let formIsValid = true;

        if (quote.siteContactName == "" || quote.siteContactPhone == "") {
            formIsValid = false;
            errorList["siteContact"] = "Site Contact Name and Phone is required";
        }

        if (quoteItem.truckDoorsId < 1 && quoteItem.isEws && quoteItem.typeId != 21 && quote.customerPickingUp === 0 ) {
            errorList["quoteItems"] = "Please select Truck Doors Direction before scheduling!";
            formIsValid = false;
        }

        //check power poles
        if (quoteItem.typeId === 23) {
            var valid = await checkPowerPoleQuestions(quoteItem.itemId, true);
            if (!valid) {
                let isUnderground = quoteItem.item.toLowerCase().includes("underground");
                var extraNote = isUnderground ? "(Must have pit on site)" : "(Must have clear line of sight)";
                errorList["quoteItems"] = "Power Pole Questions must be answered " + extraNote + " before scheduling!";
                formIsValid = false;
            }
        }

        //check epa jobs have had questions answered
        if (quoteItem.productGroupId === 6) {
            if (!quoteItem.epaQuestionsAnswered) {
                errorList["quoteItems"] = "EPA Questions must be answered before scheduling!";
                formIsValid = false;
            }
        }

        setErrors(errorList);

        return formIsValid;
    }
    //END SCHEDULE ON HOLD JOB

    //POWER POLE QUESTIONS
    const viewPowerPoleQuestions = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        var quoteItem = quote.jobQuoteItems[itemIndex];
        var canEdit = quoteItem.statusId == 1 || quoteItem.statusId == 2;

        setShowPowerPoleQuestions(true);
        setPowerPoleItemId(quoteItem.itemId);
        setPowerPoleCanEdit(canEdit);
        setCalculatorIndex(itemIndex);
    }

    const hidePowerPoleQuestions = () => {
        setShowPowerPoleQuestions(false);
        setPowerPoleItemId(-1);
        setPowerPoleCanEdit(false);
        setCalculatorIndex(-1);
    }

    const savePowerPoleQuestions = () => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[calculatorIndex];
        setShowPowerPoleQuestions(false);
        setPowerPoleItemId(-1);
        setPowerPoleCanEdit(false);
        setCalculatorIndex(-1);
        calcQuotePrice(quoteItems, quoteItem);
    }
    //END POWER POLE QUESTIONS

    //EPA QUESTIONS
    const viewEpaQuestions = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        var quoteItem = quote.jobQuoteItems[itemIndex];
        var canEdit = quoteItem.statusId == 1 || quoteItem.statusId == 2;

        setShowEpaQuestions(true);
        setJobQuoteItemId(quoteItem.id);
        setItemId(quoteItem.itemId);
        setItemDescription(quoteItem.item);
        setItemTypeId(quoteItem.typeId);
        setPowerPoleCanEdit(canEdit);
        setCalculatorIndex(itemIndex);
    }

    const hideEpaQuestions = () => {
        setShowEpaQuestions(false);
        setJobQuoteItemId(-1);
        setItemId(0);
        setItemDescription("");
        setItemTypeId(-1);
        setPowerPoleCanEdit(false);
        setCalculatorIndex(-1);
    }

    const saveEpaQuestions = (tankLidExposed: boolean, confinedSpace: boolean, weekendJob: boolean, emergencySameDayJob: boolean, updatedInvoiceDescription: string, updatedIncludeGreaseTrap: boolean) => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[calculatorIndex];
        quoteItem.invoiceDescription = updatedInvoiceDescription;
        quoteItem.trolley = updatedIncludeGreaseTrap;

        quoteItem.tankLidExposed = tankLidExposed;
        if (quoteItem.typeId === 58) {
            quoteItem.extraHours = tankLidExposed ? 0 : 0.5;        //need to charge an extra half an hour to locate tank lid
        }
        quoteItem.weekendJob = weekendJob;
        quoteItem.emergencySameDayJob = emergencySameDayJob;
        quoteItem.epaQuestionsAnswered = true;

        setShowEpaQuestions(false);
        setJobQuoteItemId(-1);
        setItemId(0);
        setItemDescription("");
        setItemTypeId(-1);
        setPowerPoleCanEdit(false);
        setCalculatorIndex(-1);

        if (quoteItem.typeId === 61) {
            quoteItem.confinedSpaceAmount = confinedSpace ? epaConfinedSpacePrice : 0;
            quoteItem.cleaningFee = weekendJob ? weekendRate : emergencySameDayJob ? sameDayRate : 0;

            //calculate amount field
            var itemAmount = quoteItem.initialHire + quoteItem.travelAmount + quoteItem.cleaningFee + quoteItem.trenchFee + quoteItem.confinedSpaceAmount + quoteItem.sampleTestingAmount + quoteItem.wasteDisposalAmount + quoteItem.fuelSurchargeAmount;
            quoteItem.amount = parseFloat(itemAmount.toFixed(2));

            var total = calculateTotal(quoteItems);
            var totalFormatted = total.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
            var totalInvoice = (total * 1) + (quoteDetails.totalInvoiceExtraAmount * 1);
            var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });
            setQuoteDetails(prevState => ({ ...prevState, jobQuoteItems: quoteItems, totalQuotedAmount: total, totalQuotedAmountFormatted: totalFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));
        } else {
            calcQuotePrice(quoteItems, quoteItem);
        }
    }
    //END EPA QUESTIONS

    //TROLLEY - add trolley to the job
    const viewTrolley = (e: any, itemIndex: number) => {
        e.preventDefault();

        var quote = quoteDetails;
        var quoteItem = quote.jobQuoteItems[itemIndex];
        var canEdit = quoteItem.statusId == 1 || quoteItem.statusId == 2;
        setShowExtraTrolley(true);
        setExtraTrolley(quoteItem.trolley);
        setExtraIndex(itemIndex);
        setEditExtraItems(canEdit);
        setItemTypeId(quoteItem.typeId);
    }

    const updateExtraTrolley = (trolley: boolean) => {
        setExtraTrolley(trolley);
    }

    const hideExtraTrolley = () => {
        setShowExtraTrolley(false);
        setExtraTrolley(false);
        setExtraIndex(-1);
        setEditExtraItems(true);
        setItemTypeId(-1);
    }

    const saveExtraTrolley = () => {
        var quoteItems = [...quoteDetails.jobQuoteItems];
        var quoteItem = quoteItems[extraIndex];
        quoteItem.trolley = extraTrolley;

        setShowExtraTrolley(false);
        setExtraTrolley(false);
        setExtraIndex(-1);
        setEditExtraItems(true);
        setItemTypeId(-1);
        calcQuotePrice(quoteItems, quoteItem);
    }
    //END TROLLEY

    //EXTRA INVOICE ITEMS
    const viewExtraInvoiceItems = (e: any) => {
        e.preventDefault();

        setExtraInvoiceItemQuoteId(quoteDetails.id);
        setShowExtraInvoiceItems(true);
    }

    const hideExtraInvoiceItems = () => {
        setExtraInvoiceItemQuoteId(0);
        setShowExtraInvoiceItems(false);
    }

    const saveExtraInvoiceItems = (totalExtraInvoiceItem: number) => {
        var quoteTotal = quoteDetails.totalQuotedAmount;
        var totalInvoiceExtra = totalExtraInvoiceItem;
        var totalInvoiceExtraFormatted = totalExtraInvoiceItem.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });   
        var totalInvoice = (quoteTotal * 1) + (totalInvoiceExtra * 1);
        var totalInvoiceFormatted = totalInvoice.toLocaleString("en-AU", { style: "currency", currency: "AUD", minimumFractionDigits: 2 });

        setQuoteDetails(prevState => ({ ...prevState, totalInvoiceExtraAmount: totalInvoiceExtra, totalInvoiceExtraAmountFormatted: totalInvoiceExtraFormatted, totalInvoiceAmount: totalInvoice, totalInvoiceAmountFormatted: totalInvoiceFormatted }));
        setExtraInvoiceItemQuoteId(0);
        setShowExtraInvoiceItems(false);
    }
    //END EXTRA INVOICE ITEMS

    const copyJob = () => {
        navigate("/newjob/" + jobTypeId + "/" + id);
    }

    const handleImportFile = (e: any) => {
        var files = e.target.files;
        //this.setState({ uploadedFiles: e.target.files });
        uploadPlans(files);
    }

    const uploadPlans = async (importFiles: File[]) => {
        var jobId = Number(id);

        const filedata = new FormData()
        filedata.append('jobId', jobId.toString());
        filedata.append('fileTypeId', "1");

        for (var i = 0; i < importFiles.length; i++) {
            filedata.append('files', importFiles[i]);
        }

        try {
            const apiUrl = "SaveJobFiles";
            var result = await PostFormDataQuery(apiUrl, filedata);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (result.data.isError) {
                    var errors = result.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    toast.success("Plan/s have been saved");
                    getQuotePlans();
                }
            }
        } catch (e) {
            toast.error("Error saving files!");
        }
    }

    //delete job plan
    const deleteJobPlan = (planid: number) => {
        setShowDeletePlan(true);
        setDeletePlanId(planid);
    }

    const hideDeleteJobPlan = () => {
        setShowDeletePlan(false);
        setDeletePlanId(-1);
    }

    const saveDeleteJobPlan = () => {
        deletePlan();
    }

    const deletePlan = async () => {
        try {
            var deleteplan = {
                id: deletePlanId,
            }

            var jsonItem = JSON.stringify(deleteplan);

            const apiUrl = "DeleteJobFile";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                toast.success("Plan Deleted");
                var planIndex = plans.findIndex(p => p.id == deletePlanId);
                plans.splice(planIndex, 1);
                setPlans(plans);
                setShowDeletePlan(false);
                setDeletePlanId(-1);
            }
        } catch (e) {
            toast.error("Error deleting plan!");
        }
    }

    //download job plan
    const downloadPlan = (e: any, planid: number) => {
        e.preventDefault();
        getPlanFile(planid);
    }

    const getPlanFile = async (planid: number) => {
        try {
            var filename = "plans.pdf";
            const apiUrl = 'GetJobFile?Id=' + planid;
            await DownloadFile(apiUrl, filename);
        } catch (e) {
            toast.error("Error getting job file!");
        }
    }

    const printJobQuoteAcceptance = () => {
        var url = "/job/" + id + "/report/JobQuoteAcceptance";
        window.open(url, '_blank');
    }

    const printJobQuote = async () => {
        //validate data before printing runsheets
        //if list is filtered we only want to print that list so only validate that list
        //ignore any items that cannot be updated
        if (await validate()) {
            //save version history
            saveVersionHistory();

            var reportName = systemCompanyId === 3 && jobTypeId === 2 ? "JobRequest" : systemCompanyId === 3 ? "JobQuoteRequest" : "JobQuote";

            var url = "/job/" + id + "/report/" + reportName;
            window.open(url, '_blank');
        } else {
            toast.error("Please fix validation errors and try Saving again!");
        }
    }

    const printJobQuoteSampleTesting = () => {
        var url = "/job/" + id + "/report/JobQuoteRequestSampleTesting";
        window.open(url, '_blank');
    }

    const saveVersionHistory = async () => {
        var saveVersion = {
            jobQuoteId: quoteDetails.id
        };
        var jsonItem = JSON.stringify(saveVersion);
        const apiUrl = "SaveQuoteVersion";
        var result = await PostQuery(apiUrl, jsonItem);            
    }

    //MOVE ROW
    const handleDragStart = (event: React.DragEvent<HTMLDivElement>, oldIndex: number) => {
        event.dataTransfer.setData('text', oldIndex.toString());
    }

    const enableDropping = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    }

    const handleDrop = (event: React.DragEvent<HTMLDivElement>, newIndex: number) => {
        const oldIndexText = event.dataTransfer.getData('text');
        var oldIndex = parseInt(oldIndexText);
        onSortEnd(oldIndex, newIndex);
    }

    const onSortEnd = (oldIndex: number, newIndex: number) => {
        var jobQuote = quoteDetails;
        var jobQuoteItems = jobQuote.jobQuoteItems;

        var hasBlankRow = jobTypeId === 1 || jobTypeId === 3;
        var quoteLength = hasBlankRow ? jobQuoteItems.length - 1 : jobQuoteItems.length;

        if (oldIndex != newIndex && ((hasBlankRow && oldIndex != quoteLength && newIndex != quoteLength) || !hasBlankRow)) {
            //only move if not moving to same location or to blank row
            jobQuoteItems.splice(newIndex, 0, jobQuoteItems.splice(oldIndex, 1)[0]);

            //loop through the items and update the order
            var itemOrder = 1;  //not used for cancelled items
            for (var i = 0; i < jobQuoteItems.length; i++) {
                jobQuoteItems[i].itemIndex = (i);
                if (jobQuoteItems[i].status !== "Cancelled") {
                    //need to ignore cancelled items
                    jobQuoteItems[i].itemOrder = (itemOrder);
                    itemOrder += 1;
                }
            }

            saveQuote("", false, -1);
        }
    };
    //END MOVE ROW

    const validate = async () => {
        let errorList: any = {};
        let formIsValid = true;

        //check to make sure there is only one power pole item
        var ppItems = quoteDetails.jobQuoteItems.filter(i => i.typeId === 23 && i.statusId === 1);
        if (ppItems.length > 1) {
            formIsValid = false;
            errorList["quoteItems"] = "ONLY 1 Power Pole Item allowed per quote! Please remove any extra power poles!";
            setErrors(errorList);
            return formIsValid;
        }

        let items = quoteDetails.jobQuoteItems;
        var rowNumber = 0;

        var errorString = "Please enter the following values: ";
        var error = false;
        //last row is a blank new row so ignore
        for (var i = 0; i < items.length - 1; i++) {
            //update the row number depending on whether we have filtered out cancelled items
            if ((showCancelledItems) || (!showCancelledItems && items[i].status != "Cancelled") ) {
                rowNumber += 1;
            } 
            //ignore any installed, pickedup, cancelled items
            if (items[i].statusId == 1 || items[i].statusId == 2) {
                var newErrorString = "";
                if (items[i].itemId < 0) {
                    newErrorString += " Item, ";
                    error = true;
                }
                if (!items[i].callout && items[i].hirePeriod <= 0) {
                    newErrorString += " Hire Period, ";
                    error = true;
                }

                if (items[i].itemId > 0 && items[i].isVoid && items[i].size <= 0) {
                    newErrorString += " Void Size, ";
                    error = true;
                }

                if (items[i].itemId > 0 && items[i].isTower && items[i].size <= 0) {
                    newErrorString += " Tower Height, ";
                    error = true;
                }

                if (items[i].typeId === 38 && (!items[i].extraHours || items[i].extraHours <= 0)) {
                    newErrorString += " Hours, ";
                    error = true;
                }

                if (items[i].productGroupId === 6 && (!items[i].size || items[i].size <= 0)) {
                    newErrorString += " Size, ";
                    error = true;
                }

                if (items[i].quantity <= 0) {
                    newErrorString += " Quantity, ";
                    error = true;
                }
                if (items[i].amount <= 0) {
                    newErrorString += " Amount, ";
                    error = true;
                }

                let isElite = items[i].item.toLowerCase().includes("elite");
                if (items[i].truckId < 1 && items[i].isEws && items[i].typeId != 21) {
                    newErrorString += " Truck, ";
                    error = true;
                } else if (isElite && items[i].truckId != 1) {
                    newErrorString += " Elite Units MUST use Tilt Truck, ";
                    error = true;
                }

                //check power poles
                if (items[i].typeId === 23) {
                    var valid = await checkPowerPoleQuestions(items[i].itemId, false);
                    if (!valid) {
                        newErrorString += " Power Pole Questions must be answered, ";
                        error = true;
                    }
                }

                //check truck hours for epa jobs
                if (items[i].typeId === 61) {
                    if (items[i].extraHours < 1) {
                        newErrorString += " Truck Hours, ";
                        error = true;
                    }
                    if (items[i].initialHire <= 0 ) {
                        newErrorString += " Initial Service Price, ";
                        error = true;
                    }
                }

                //overhire rate can be $0 for install only items
                var installOnly = false;
                if (items[i].itemId > 0) {
                    //find item option
                    var itemRow = itemOptions.find(io => io.id === items[i].itemId);
                    if (itemRow) {
                        installOnly = itemRow.installOnly;
                    }
                }

                //Callouts, Events and Install Only items don't need overhire rates
                if (!items[i].callout && items[i].overhireRate <= 0 && items[i].extendable && !installOnly && !quoteDetails.isEvent) {
                    newErrorString += " Overhire Rate, ";
                    error = true;
                }

                if (!items[i].callout && items[i].amount > 0 && items[i].overhireRate > 0) {
                    //check to make sure if an amount is entered that there is also an overhire rate and vice versa
                    var amount = items[i].amount * 1;//parseInt(items[i].amount, 10);
                    var overhire = items[i].overhireRate * 1; //parseInt(items[i].overhireRate, 10);

                    if (amount === 0 && overhire > 0) {
                        newErrorString += " Amount cannot be $0, ";
                        error = true;
                    }
                }

                if (newErrorString != "") {
                    errorString += "\n"
                    errorString += "Row " + rowNumber + ": " + newErrorString;
                    errorString = errorString.substring(0, errorString.length - 2);
                }
            }
        }
        if (error) {
            formIsValid = false;
            errorList["quoteItems"] = errorString;
        }
        setErrors(errorList);
        return formIsValid;
    }

    const checkPowerPoleQuestions = async (itemId: number, isScheduling: boolean) => {
        try {
            const apiUrl = 'ValidatePowerPoleQuestions?JobId=' + id + '&itemId=' + itemId + '&isScheduling=' + isScheduling;
            var result = await GetQuery(apiUrl);

            if (result.error) {
                toast.error("Error getting data!");
            } else {
                if (result.data) {
                    return true;
                }
            }
        } catch (e) {
            toast.error("Error checking power pole questions!");
        }

        return false;
    }

    const submit = (e: any) => {
        e.preventDefault();
        checkQuote("");
    }

    const checkQuote = async (calledFrom: string) => {
        let valid = await validate();

        if (!valid) {
            toast.error("Please fix validation errors before Saving!");
        } else {
            if (saveDisabled) {
                return;
            }
            setSaveDisabled(true);

            saveQuote(calledFrom, false, -1);
        }
    }

    const saveQuote = async (calledFrom: string, onHoldJob: boolean, scheduleIndexId: number) => {
        var jobQuote = quoteDetails;
        //remove blank row from item list
        var itemCopy = [...jobQuote.jobQuoteItems];
        if (jobTypeId === 1 || jobTypeId === 3) {
            itemCopy.pop();
        }

        //remove any cancelled items with an id of 0 so we don't send bad data to the database
        for (var i = 0; i < itemCopy.length; i++) {
            if (itemCopy[i].status == "Cancelled" && itemCopy[i].id == 0) {
                itemCopy.splice(i, 1);
            }
            //update custom hire period to save days not weeks
            itemCopy[i].hirePeriod = itemCopy[i].customHirePeriod && itemCopy[i].invoicePeriodTypeId === 1 ? itemCopy[i].hirePeriod * 7 : itemCopy[i].hirePeriod;
        }

        var quoteToSave = {
            jobId: jobQuote.jobId,
            quoteNo: jobQuote.quoteNo,
            quoteNotes: jobQuote.quoteNotes,
            manualQuoteNumber: jobQuote.manualQuoteNumber,
            requireSiteVisit: jobQuote.requireSiteVisit,
            siteVisitPerson: jobQuote.requireSiteVisit ? jobQuote.siteVisitPerson : 0,
            siteContactName: jobQuote.siteContactName,
            siteContactPhone: jobQuote.siteContactPhone,
            showSiteVisitNote: jobQuote.showSiteVisitNote,
            customerPickingUp: jobQuote.customerPickingUp,
            jobQuoteItems: itemCopy,
            fenceArea: fenceArea
        }

        //save
        try {
            var jsonItem = JSON.stringify(quoteToSave);

            const apiUrl = "SaveQuote";
            var result = await PostQuery(apiUrl, jsonItem);
            if (result.error) {
                toast.error("Error saving data!");
            } else {
                if (calledFrom === "schedule") {
                    //need to schedule the item now that we have saved it
                    var quoteItem = itemCopy[scheduleIndexId];
                    quoteItem.isDirty = false;
                    schedOnHoldQuotedJob(jobQuote, quoteItem, onHoldJob);
                } else {
                    toast.success("Quote Saved");
                    getQuote();
                }
            }
        } catch (e) {
            toast.error("Error saving quote!");
        }
    }

    const tabChanged = (url: string) => {
        navigate(url);
    }

    const jobTab = <JobTabsData jobId={Number(id)} siteAddress={siteAddress} tab={"quote"} tabChanged={tabChanged} />;

    let showButton = false;
    if (!quoteDetails.quoteNo) {
        showButton = true;
    }
    let filteredItems = quoteDetails.jobQuoteItems.filter(data =>
        data.status != "Cancelled"
    )
    let quoteItems = showCancelledItems ? quoteDetails.jobQuoteItems : filteredItems;
    var lastRowIndex = jobTypeId === 2 ? quoteItems.length : quoteItems.length - 1;
    let hasEpaItems = quoteItems.filter(i => i.productGroupId === 6).length > 0;
    let hasEpaOtherItems = quoteItems.filter(i => i.typeId === 61).length > 0;
    let confirmDeletePlan = <ConfirmPopups heading="Delete Plan" text="Are you sure you want to delete this plan?" showConfirmModal={showDeletePlan} hideConfirmModal={hideDeleteJobPlan} saveConfirmModal={saveDeleteJobPlan} />
    
    let notesPopup = <NotesPopup showEditNotesModal={showEditNotesModal} editNotes={editNotes} hideNotes={hideNotes} updateNotes={updateNotes} saveNotes={saveNotes} />
    let quoteNotePopup = <QuoteNotePopup showModal={showQuoteNote} notes={quoteNote} hideModal={hideQuoteNote} update={updateQuoteNote} saveModal={saveQuoteNote} />
    let calculatorPopup = <JobQuoteCalculatorModal showModal={showCalculator} starterBayOptions={starterBayOptions} liftOptions={liftOptions} kwikstageInstalledForOptions={kwikstageInstalledForOptions} statusId={calculatorStatusId} kwikstageItem={calculatorKwikstageItem} items={calculatorItems} editOnly={false} canEditBays={calculatorStatusId === 1 || calculatorStatusId === 2} typeId={-1} hideModal={hideCalculateKwikstage} update={updateCalculateKwikstage} updateItem={updateCalculatorKwikstageItem} save={saveCalculateKwikstage} />
    let siteNotesPopup = <QuoteSiteNotePopup showModal={showAddSiteNotes} jobQuoteId={quoteDetails.id} notes={siteNotes} hideModal={hideSiteNotes} update={updateSiteNotes} />
    //is Event is set to false here as we don't set delivery address until the item is scheduled
    let extrasPopup = <JobQuoteExtraItemModal isEvent={false} existDeliveryAddress={""} showModal={showExtraItems} extraItemOptions={extraItemOptions} itemDescription={itemDescription} itemTableDesk={tables} itemChairs={chairs} readonly={!editExtraItems} extras={extras} hideModal={hideExtraItems} update={updateExtraItems} save={saveExtraItems} />
    let extraSections = <JobQuoteExtraSectionData showModal={showExtraSection} crossoverTotal={extraSection} readOnly={!editExtraItems} update={updateExtraSection} hideModal={hideExtraSection} saveModal={saveCrossoverTotal} />
    let schOnHoldJob = <AllocateScheduleJobPopup showModal={showScheduleJob} customerNewFuelInvoice={customerNewFuelInvoice} scheduleItemDetail={scheduleItemDetail} hideModal={hideScheduleOnHoldJob} saveModal={saveScheduleOnHoldJob} />
    let powerPoleQuestions = <JobQuoteQuestionsData showModal={showPowerPoleQuestions} readOnly={!powerPoleCanEdit} jobId={Number(id)} itemId={powerPoleItemId} yesNoOptions={yesNoOptions} hideModal={hidePowerPoleQuestions} saveModal={savePowerPoleQuestions} />
    let extraTrolleyItem = <JobQuoteExtraTrolleyData showModal={showExtraTrolley} readOnly={!editExtraItems} trolley={extraTrolley} isEpa={itemTypeId === 58} update={updateExtraTrolley} hideModal={hideExtraTrolley} saveModal={saveExtraTrolley} />
    let extraInvItems = <JobQuoteExtraInvoiceItemModal showModal={showExtraInvoiceItems} jobId={Number(id)} jobQuoteId={extraInvoiceItemQuoteId} hideModal={hideExtraInvoiceItems} save={saveExtraInvoiceItems} />
    let pitchHeading = "Pitch > 26";
    let jobQuoteHeading = systemCompanyId === 3 && jobTypeId === 2 ? "Print Job Request" : "Print Job Quote";
    let delAddr: DeliveryAddress = {
        deliveryAddress: "",
        deliveryAddressGooglePlaceId: "",
        deliveryAddressLatitude: 0,
        deliveryAddressLongitude: 0
    };
    let invoiceItemPopup = <JobQuoteDeliveryAddressData deliveryAddress={delAddr} showDeliveryAddress={false} invoiceDescription={existInvoiceDescription} showInvoiceDescription={true} trolleyRequired={trolley} showTrolley={false} readOnly={editTrolleyReadOnly} showModal={showInvoiceDescription} hideModal={hideInvoiceDescription} updateDeliveryAddress={updateDeliveryAddress} clearDeliveryAddress={clearDeliveryAddress} updateInvoiceDescription={updateInvoiceDescription} updateTrolley={updateTrolley} saveModal={saveInvoiceDescription} />
    let checkWalkWayPitchItem = <JobQuoteWalkWayModal walkWayItem={walkWayItem} canEdit={walkwayStatusId === 1 || walkwayStatusId === 2} hideModal={hideWalkWayPitch} showModal={showWalkwayPitch} update={updateWalkWayPitch} save={saveWalkWayPitch} />
    let fencingBracing = <JobQuoteBraceModal showModal={showFenceBracing} statusId={calculatorStatusId} hideModal={hideFenceBracing} save={saveFenceBracing} />
    let epaQuestions = <JobQuoteEpaQuestionsModalData showModal={showEpaQuestions} isReadOnly={!powerPoleCanEdit} jobId={Number(id)} jobQuoteItemId={jobQuoteItemId} itemId={itemId} itemTypeId={itemTypeId} item={itemDescription} hideModal={hideEpaQuestions} saveModal={saveEpaQuestions} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {notesPopup}
                {calculatorPopup}
                {quoteNotePopup}
                {confirmDeletePlan}
                {siteNotesPopup}
                {extrasPopup}
                {powerPoleQuestions}
                {extraTrolleyItem}
                {fencingBracing}
                {epaQuestions}
            </div>
            <div>
                {checkWalkWayPitchItem}
                {extraSections}
                {schOnHoldJob}
                {extraInvItems}
                {invoiceItemPopup}
            </div>
            <div className="static-modal">
                <Modal show={showCancelModal} onHide={hideCancelModal}>
                    <Modal.Header className="modal__header--colour">
                        <Modal.Title>Cancel {cancelItem}</Modal.Title>
                        <button type="button" className="close" onClick={hideCancelModal}>&times;</button>
                    </Modal.Header>
                    <Modal.Body>
                        <label className="input-group" htmlFor="cancelReason">
                            <span className="label">Reason</span>
                            <select className="select" id="cancelReason" name="cancelReason" value={cancelReasonId} onChange={(e) => handleCancelReasonChange(e)} >
                                <option defaultValue="-1"></option>
                                {cancelOptions.map(reason =>
                                    <option key={reason.id} value={reason.id}>{reason.name}</option>
                                )}
                            </select>
                        </label>
                        <span className="label errors">{errors["cancelReason"]}</span>
                    </Modal.Body>

                    <Modal.Footer>
                        <button className="popup-button--yes" onClick={saveCancel}>Save</button>
                    </Modal.Footer>
                </Modal>
            </div>
            {jobTab}
            <div className={showButton ? "" : "hidden"}>
                <button className="defaultbutton defaultbutton__small form-margin" onClick={createQuote} type="button">New Quote</button>
            </div>
            <div className={showButton ? "hidden" : ""}>
                <div className="makeitflexspacebetween">
                    <div>
                        <div>
                            <label className="input-group--inline" htmlFor="quoteNo">
                                <span className="label label--inline">Quote No</span>
                                <input className="input" type="text" id="quoteNo" name="quoteNo" value={quoteDetails.quoteNo} onChange={(e) => handleChange(e)} disabled></input>
                            </label>

                            <label className="input-group--inline" htmlFor="manualQuoteNumber">
                                <span className="label label--inline">Manual Quote No</span>
                                <input className="input" type="text" id="manualQuoteNumber" name="manualQuoteNumber" value={quoteDetails.manualQuoteNumber} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <label className="input-group--inline job__checkboxPosition" htmlFor="showCancelledItems">
                                <span className="label label--inline">Show Cancelled Items </span>
                                <input type="checkbox" name="showCancelledItems" id="showCancelledItems" checked={showCancelledItems} onChange={(e) => setShowCancelledItems(e.target.checked)}></input>
                            </label>
                            <span className="marginLeft10">{customerAccountType}</span><span className={customerAccountTypeId === 1 ? "hidden" : ""}> - {customerAccountLimitFormatted}</span>
                        </div>
                        <div>
                            <label className="input-group--inline" htmlFor="siteContactName">
                                <span className="label label--inline">Site Contact Name</span>
                                <input className="input" type="text" maxLength={50} id="siteContactName" name="siteContactName" value={quoteDetails.siteContactName} onChange={(e) => handleChange(e)}></input>
                            </label>

                            <label className="input-group--inline" htmlFor="siteContactPhone">
                                <span className="label label--inline">Site Contact Phone</span>
                                <input className="input" type="text" maxLength={50} id="siteContactPhone" name="siteContactPhone" value={quoteDetails.siteContactPhone} onChange={(e) => handleChange(e)}></input>
                            </label>
                            <label className={systemCompanyId === 3 ? "hidden" : "input-group--inline job__checkboxPosition"} htmlFor="requireSiteVisit">
                                <span className="label label--inline">Require Site Visit?</span>
                                <input type="checkbox" name="requireSiteVisit" id="requireSiteVisit" checked={quoteDetails.requireSiteVisit} onChange={(e) => handleSiteVisitCheckboxChange(e)}></input>
                            </label>
                            <label className={systemCompanyId === 3 ? "input-group--inline" : "hidden"} htmlFor="customerPickingUp">
                                <span className="label label--inline">Customer picking up?</span>
                                <select className="select" id="customerPickingUp" name="customerPickingUp" value={quoteDetails.customerPickingUp} onChange={(e) => handleCustPickupChange(e)}>
                                    <option hidden defaultValue="-1"></option>
                                    {yesNoOptions.map(yn =>
                                        <option key={yn.id} value={yn.id}>{yn.name}</option>
                                    )};
                                </select>
                            </label>
                            <label className="input-group--inline">
                                <a href="#" onClick={(e) => viewQuoteHistory(e)}>View Quote History</a>
                            </label>
                            <span className={errors["siteContact"] ? "label errors" : "hidden"}>{errors["siteContact"]}</span>
                        </div>
                    </div>
                    <div>
                        <label className="input-group" htmlFor="quoteNotes">
                            <span className="label">Quote Notes</span>
                            <textarea className="input quote-notes--size" rows={4} id="quoteNotes" name="quoteNotes" value={quoteDetails.quoteNotes} onChange={(e) => handleChange(e)}></textarea>
                        </label>
                    </div>
                </div>
                <div className="table__minHeight overflowAuto">
                    <table className="table table__large tableColours marginTop10">
                        <thead>
                            <tr>
                                <th className="hidden">Id</th>
                                <th></th>
                                <th></th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}></th>
                                <th className="table-input--minwidth100">Item</th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}>Extra</th>
                                <th></th>
                                <th className={systemCompanyId === 2 ? "" : "hidden"}>{pitchHeading}</th>
                                <th>Status</th>
                                <th>Hire Period {systemCompanyId === 2 ? "(days)" : ""}</th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}></th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}>Maint/Hrs/ Bracing/Truck</th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}>
                                    <div className="tooltipitem tooltipitem__right">Size /Doors Facing?
                                        <span className="tooltipitemtext tooltipitemtext__right">Which way are the doors to face? (remembering the tilt slide reverses into position)</span>
                                    </div>
                                </th>
                                <th className={systemCompanyId === 2 ? "" : "hidden"}>Size</th>
                                <th className={systemCompanyId === 2 ? "" : "hidden"}>
                                    <div className="tooltipitem tooltipitem__right">Ins/Dis
                                        <span className="tooltipitemtext tooltipitemtext__right">Include Install and Dismantle Cost</span>
                                    </div>
                                </th>
                                <th></th>
                                <th>Quantity</th>
                                <th>Type</th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}>Initial</th>
                                <th>Travel ($)</th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}>Clean/Connect /Callout</th>
                                <th className={systemCompanyId === 3 ? "" : "hidden"}>Trench/Truck Hire Fee</th>
                                <th className={systemCompanyId === 3 && hasEpaItems ? "" : "hidden"}>Confined Space</th>
                                <th className={systemCompanyId === 3 && hasEpaItems ? "" : "hidden"}>Sample Testing</th>
                                <th className={systemCompanyId === 3 && hasEpaItems ? "" : "hidden"}>Waste Disposal</th>
                                <th>Fuel Surcharge($)</th>
                                <th className={systemCompanyId === 2 ? "" : "hidden"}>Extra Hours</th>
                                <th className="hidden">Rate ($)</th>
                                <th className="hidden">GST ($)</th>
                                <th>Amount ($)</th>
                                <th>Overhire Rate ($)</th>
                                <th className={systemCompanyId === 2 ? "" : "hidden"}>Est. Cost</th>
                                <th className={systemCompanyId === 2 ? "" : "hidden"}>Est. Margin</th>
                                <th className={systemCompanyId === 2 ? "" : "hidden"}>Est. Margin %</th>
                                <th>Cancel</th>
                                <th className="table-input--minwidth100">Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {quoteItems.map((item, index) =>
                                <tr key={index} draggable="true" onDragStart={(e) => handleDragStart(e, item.itemIndex)} onDragOver={enableDropping} onDrop={(e) => handleDrop(e, item.itemIndex)}>
                                    <td className="hidden">{item.id}</td>
                                    <td className="table__text--align">{index + 1}</td>
                                    <td className="table__text--align">
                                        <input name="showOnQuote" type='checkbox' className={lastRowIndex != index ? "input" : "hidden"} checked={item.showOnQuote} onChange={(e) => onCellCheckboxChange(e, item.itemIndex)} />
                                    </td>
                                    <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <span className="tooltipitem tooltipitem__right">
                                            <i className={lastRowIndex != index && item.id > 0 && item.typeId != 43 && (item.statusId == 1 || item.statusId == 2) ? "fa-solid fa-calendar-days alignIconCenter makeitgreen" : "hidden"} onClick={(e) => scheduleOnHoldJob(e, item.itemIndex, false)}></i>
                                            <span className="tooltipitemtext tooltipitemtext__right">Schedule Item</span>
                                        </span>
                                    </td>
                                    <td className="table__text--align">
                                        <select className={item.statusId == 1 || item.statusId == 2 ? "select table__select--size allocate__item--maxwidth" : "hidden"} id="itemId" name="itemId" value={item.itemId} onChange={(e) => onCellItemChange(e, lastRowIndex == index, item.itemIndex)} onBlur={(e) => item.itemId === 4 ? calculateKwikstage(e, item.itemIndex) : checkWalkWayPitch(e, item.itemIndex)} disabled={item.statusId == 1 || item.statusId == 2 ? false : true}>
                                            <option hidden defaultValue="-1"></option>
                                            {itemOptions.map(item =>
                                                quoteDetails.isEvent || (!quoteDetails.isEvent && item.typeId != 33 && item.typeId != 42) ?
                                                    <option key={item.id} value={item.id}>{item.name}</option>
                                                : ""
                                            )};
                                        </select>
                                        {item.statusId == 1 || item.statusId == 2 ? "" : item.invoiceDescription != "" ? item.invoiceDescription : item.item}
                                    </td>
                                    <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <a className={item.typeId === 14 || item.typeId === 17 ? "icon--cellwidth tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => addExtraItems(e, item.itemIndex)}>
                                            <span className={"fa-solid fa-circle-plus edit--icon alignIconCenter" + (item.extras.quantity > 0 || item.extras.equipped ? " makeitgreen" : "")}></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Add equipment and/or extra Table/Desk and Chairs</span>
                                        </a>
                                        <a className={item.typeId === 23 ? "icon--cellwidth tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => viewPowerPoleQuestions(e, item.itemIndex)}>
                                            <span className="fa-solid fa-circle-question edit--icon alignIconCenter makeitgreen"></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Answer Pole Pole Questions</span>
                                        </a>
                                        <a className={item.typeId === 27 ? "icon--cellwidth tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => calculateCrossoverQuantity(e, item.itemIndex)}>
                                            <span className={"fa-solid fa-circle-plus edit--icon alignIconCenter" + (item.extras.quantity > 0 ? " makeitgreen" : "")}></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Calculate Crossover Sections</span>
                                        </a>
                                        <a className={item.typeId === 25 || item.typeId === 29 || item.typeId === 44 ? "icon--cellwidth tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => viewTrolley(e, item.itemIndex)}>
                                            <span className={"fa-solid fa-circle-plus edit--icon alignIconCenter" + (item.trolley ? " makeitgreen" : "")}></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Add a Trolley to the job</span>
                                        </a>
                                        <a className={(item.isEvent || item.typeId === 52) && item.id > 0 ? "icon--cellwidth tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => editInvoiceDescription(e, item.itemIndex)}>
                                            <span className={"fa-solid fa-circle-plus edit--icon alignIconCenter " + (item.invoiceDescription != "" ? " makeitgreen" : "")}></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Update the item name for displaying on the quote and invoice</span>
                                        </a>
                                        <a className={item.typeId === 50 || item.typeId === 51 ? "icon--cellwidth makeitgreen tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => openFenceBracing(e, item.itemIndex)}>
                                            <span className="fas fa-calculator edit--icon alignIconCenter"></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Check/Calculate bracing required for fencing</span>
                                        </a>
                                        <a className={item.typeId > 57 && item.typeId < 62 && item.id > 0 ? "icon--cellwidth tooltipitem tooltipitem__right" : "hidden"} href="#" onClick={(e) => viewEpaQuestions(e, item.itemIndex)}>
                                            <span className="fa-solid fa-circle-question edit--icon alignIconCenter makeitgreen"></span>
                                            <span className="tooltipitemtext tooltipitemtext__right">Answer EPA Questions</span>
                                        </a>
                                    </td>
                                    <td className="table__text--align">
                                        {item.itemId === 4 || item.isWalkWay ?
                                            <div className="delete--tablecell">
                                                <a className="makeitgreen" href="#" onClick={(e) => item.itemId === 4 ? calculateKwikstage(e, item.itemIndex) : checkWalkWayPitch(e, item.itemIndex)}>
                                                    <span className="fas fa-calculator edit--icon alignIconCenter"></span>
                                                </a>
                                            </div> :
                                            <div className={systemCompanyId === 3 ? "hidden" : "delete--tablecell"}>
                                                <a className={lastRowIndex != index ? !item.quoteNote ? "" : "makeitgreen" : "hidden"} href="#" onClick={(e) => addQuoteNotes(e, item.itemIndex)}>
                                                    <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                                </a>
                                            </div>
                                        }
                                    </td>
                                    <td className={systemCompanyId === 2 ? "table__text--align textaligncenter" : "hidden"}>
                                        <select className={item.isWalkWay ? "select table__select--size" : "hidden"} id="steepRoofPitch" name="steepRoofPitch" value={item.steepRoofPitch} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "steepRoofPitch", Number(e.target.value))} disabled={item.statusId == 1 || item.statusId == 2 ? false : true}>
                                            <option value={1}>Yes</option>
                                            <option value={0}>No</option>
                                        </select>
                                    </td>
                                    <td className="table__text--align">
                                        {item.status}
                                        <button type="button" className={item.statusId === 9 ? "defaultbutton--green paddingFull5" : "hidden"} onClick={(e) => scheduleOnHoldJob(e, item.itemIndex, true)} >Schedule </button>
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="1" className={systemCompanyId === 3 && (!item.customHirePeriod || item.statusId > 2) ? "hidden" : "form-control textalignright"} value={item.hirePeriod} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "hirePeriod", Number(e.target.value))} disabled={item.statusId == 1 || item.statusId == 2 ? false : true} />
                                        <select className={systemCompanyId === 3 && !item.isEvent && item.statusId <= 2 && !item.customHirePeriod ? "select table__select--size" : "hidden"} id="hirePeriod" name="hirePeriod" value={item.hirePeriod} onChange={(e) => onHireCellChange(lastRowIndex == index, item.itemIndex, e)} disabled={item.callout || (item.hirePeriod === 1 && !item.customHirePeriod) || item.typeId === 53 || item.statusId > 2}>
                                            <option hidden defaultValue="-1"></option>
                                            <option hidden={!item.callout && item.typeId != 26} value={1}>1 Day</option>
                                            <option hidden={item.defaultHirePeriod > 7 || item.invoicePeriodTypeId === 2} value={7}>1 Week</option>
                                            <option hidden={item.defaultHirePeriod > 14 || item.invoicePeriodTypeId === 2} value={14}>2 Weeks</option>
                                            <option hidden={item.defaultHirePeriod > 21 || item.invoicePeriodTypeId === 2} value={21}>3 Weeks</option>
                                            <option hidden={item.defaultHirePeriod > 28 || item.invoicePeriodTypeId === 2} value={28}>4 Weeks</option>
                                            <option hidden={item.defaultHirePeriod > 35 || item.invoicePeriodTypeId === 2} value={35}>5 Weeks</option>
                                            <option hidden={item.typeId === 19 || item.typeId === 23 || item.defaultHirePeriod > 42 || item.invoicePeriodTypeId === 2} value={42}>6 Weeks</option>
                                            <option hidden={item.typeId === 23 || item.typeId === 25 || item.typeId == 26 || item.defaultHirePeriod > 56 || item.invoicePeriodTypeId === 2} value={56}>8 Weeks</option>
                                            <option hidden={item.typeId === 19 || item.typeId === 23 || item.defaultHirePeriod > 70 || item.invoicePeriodTypeId === 2} value={70}>10 Weeks</option>
                                            <option hidden={item.invoicePeriodTypeId === 2} value={91}>13 Weeks</option>
                                            <option hidden={item.typeId === 23 || item.typeId === 50 || item.typeId === 51} value={98}>14 Weeks</option>
                                            <option hidden={item.typeId === 23 || item.typeId === 50 || item.typeId === 51} value={112}>16 Weeks</option>
                                            <option hidden={item.typeId != 25 || item.invoicePeriodTypeId === 2} value={140}>20 Weeks</option>
                                            <option value={item.invoicePeriodTypeId === 2 ? 6 : 182}>{item.invoicePeriodTypeId === 2 ? "6 Months" : "26 Weeks"}</option>
                                            <option hidden={item.typeId != 23} value={252}>36 Weeks</option>
                                            <option hidden={item.typeId != 25} value={280}>40 Weeks</option>
                                            <option value={item.invoicePeriodTypeId === 2 ? 12 : 364}>{item.invoicePeriodTypeId === 2 ? "12 Months" : "52 Weeks"}</option>
                                        </select>
                                        {systemCompanyId === 3 && !item.isEvent && item.statusId > 2 ? item.hirePeriodWeeksDesc : ""}
                                    </td>
                                    <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <div className={item.customHirePeriod || item.itemId < 1 || item.isEvent || (item.hirePeriod === 1 && !item.customHirePeriod) || item.typeId === 53 || item.statusId > 2 ? "hidden" : "tooltipitem tooltipitem__right"}><i className="fa-solid fa-pen-to-square" onClick={(e) => editCustomHirePeriod(e, item.itemIndex)}></i>
                                            <span className="tooltipitemtext tooltipitemtext__right">Enter Hire Period in Weeks</span>
                                        </div>
                                    </td>
                                    <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <select className={!item.item.toLowerCase().includes("sewer connect") && (item.typeId === 25 || item.typeId === 26 || (item.typeId > 57 && item.typeId < 61)) ? "select" : "hidden"} id="frequencyId" name="frequencyId" value={item.frequencyId} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "frequencyId", Number(e.target.value))} disabled={item.statusId == 1 || item.statusId == 2 ? false : true}>
                                            <option defaultValue="-1"></option>
                                            {frequencyOptions.map(freq =>
                                                <option key={freq.id} hidden={(item.typeId === 59 && (freq.id < 4 || freq.id > 8)) || (item.typeId === 58 && freq.id < 9) || ((item.typeId < 58 || item.typeId > 59) && freq.id > 3)} value={freq.id}>{freq.name}</option>
                                            )};
                                        </select>
                                        <input className={item.typeId === 38 || item.typeId === 61 ? 'form-control textalignright table-input--minwidth' : "hidden"} type='number' min="0" step="1" id="extraHours" name="extraHours" value={item.extraHours} onChange={(e) => onCellHoursChange(e, lastRowIndex == index, item.itemIndex)} disabled={item.statusId == 1 || item.statusId == 2 ? false : true} />

                                        <select className={item.typeId === 51 ? "select allocate__size130" : "hidden"} id="fenceBraceId" name="fenceBraceId" value={item.fenceBraceId} onChange={(e) => bracingChange(lastRowIndex == index, item.itemIndex, Number(e.target.value))} disabled={item.statusId == 1 || item.statusId == 2 ? false : true} >
                                            <option value={-1} defaultValue={-1}></option>
                                            <option value={1}>Brace Every Panel</option>
                                            <option value={2}>Brace Every 2nd Panel</option>
                                            <option value={3}>Brace Every 3rd Panel</option>
                                            <option value={4}>Brace Custom</option>
                                        </select>

                                        <select className={item.isEws ? "select" : "hidden"} id="truckId" name="truckId" value={item.truckId} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "truckId", Number(e.target.value))} disabled={item.statusId == 1 || item.statusId == 2 ? false : true}>
                                            <option hidden defaultValue="-1"></option>
                                            {truckOptions.map(truck =>
                                                <option key={truck.id} value={truck.id}>{truck.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="1" className={item.productGroupId === 6 ? 'form-control textalignright table-input--minwidth100' : "hidden"} value={item.size} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "size", Number(e.target.value))} disabled={(item.statusId == 1 || item.statusId == 2) && item.itemId != 4 ? false : true} />
                                        <select className={item.isEws && item.typeId != 21 ? "select" : "hidden"} id="truckDoorsId" name="truckDoorsId" value={item.truckDoorsId} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "truckDoorsId", Number(e.target.value))} disabled={item.statusId == 1 || item.statusId == 2 ? false : true}>
                                            <option hidden defaultValue="-1"></option>
                                            {truckSideOptions.map(ts =>
                                                <option key={ts.id} value={ts.id}>{ts.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className={systemCompanyId === 2 ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="1" className={item.isVoid ? 'form-control textalignright' : "hidden"} value={item.size} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "size", Number(e.target.value))} disabled={(item.statusId == 1 || item.statusId == 2) && item.itemId != 4 ? false : true} />
                                        <select className={item.isTower ? "select" : "hidden"} id="size" name="size" value={item.size} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "size", Number(e.target.value))} disabled={(item.statusId == 1 || item.statusId == 2) && item.itemId != 4 ? false : true}>
                                            <option hidden defaultValue="-1"></option>
                                            {towerSizeOptions.map(ts =>
                                                <option key={ts.id} value={ts.id}>{ts.name}</option>
                                            )};
                                        </select>
                                    </td>
                                    <td className={systemCompanyId === 2 ? "table__text--align" : "hidden"}>
                                        <select className={item.isTower ? "select table__select--size" : "hidden"} id="includeInstallDismantle" name="includeInstallDismantle" value={item.includeInstallDismantle} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "includeInstallDismantle", Number(e.target.value))} disabled={item.statusId == 1 || item.statusId == 2 ? false : true}>
                                            <option value={1}>Yes</option>
                                            <option value={0}>No</option>
                                        </select>
                                    </td>
                                    <td className="table__text--align">{item.isVoid ? "m2" : ""}</td>
                                    <td className="table__text--align"><input type='number' min="0" step="1" className='form-control table-input--minwidth textalignright' value={item.quantity} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "quantity", Number(e.target.value))} disabled={(item.statusId == 1 || item.statusId == 2) && !(item.typeId === 58 && item.item.includes("Septic")) ? false : true} /></td>
                                    <td className="table__text--align">{item.unitOfMeasure}</td>
                                    <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className='form-control allocate__size100 textalignright' id="initialHire" name="initialHire" value={item.initialHire} onChange={(e) => onCellPriceItemChange(e, lastRowIndex == index, item.itemIndex)} disabled={(item.statusId == 1 || item.statusId == 2) ? false : true} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control textalignright table-input--minwidth' id="travelAmount" name="travelAmount" value={item.travelAmount} onChange={(e) => onCellPriceItemChange(e, lastRowIndex == index, item.itemIndex)} disabled={(item.statusId == 1 || item.statusId == 2) && item.canEditTravel ? false : true} />
                                    </td>
                                    <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className='form-control allocate__size100 textalignright' id="cleaningFee" name="cleaningFee" value={item.cleaningFee} onChange={(e) => onCellPriceItemChange(e, lastRowIndex == index, item.itemIndex)} disabled={(item.statusId == 1 || item.statusId == 2) && (item.isEws || item.productGroupId === 6) ? false : true} />
                                    </td>
                                    <td className={systemCompanyId === 3 ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className='form-control allocate__size100 textalignright' id="trenchFee" name="trenchFee" value={item.trenchFee} onChange={(e) => onCellPriceItemChange(e, lastRowIndex == index, item.itemIndex)} disabled={(item.statusId == 1 || item.statusId == 2) && item.isEws ? false : true} />
                                    </td>
                                    <td className={systemCompanyId === 3 && hasEpaItems ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className='form-control allocate__size100 textalignright' id="confinedSpaceAmount" name="confinedSpaceAmount" value={item.confinedSpaceAmount} onChange={(e) => onCellPriceItemChange(e, lastRowIndex == index, item.itemIndex)} disabled={(item.statusId == 1 || item.statusId == 2) && item.typeId === 61 ? false : true} />
                                    </td>
                                    <td className={systemCompanyId === 3 && hasEpaItems ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className='form-control allocate__size100 textalignright' id="sampleTestingAmount" name="sampleTestingAmount" value={item.sampleTestingAmount} onChange={(e) => onCellPriceItemChange(e, lastRowIndex == index, item.itemIndex)} disabled={(item.statusId == 1 || item.statusId == 2) && item.typeId === 61 ? false : true} />
                                    </td>
                                    <td className={systemCompanyId === 3 && hasEpaItems ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="0.01" className='form-control allocate__size100 textalignright' id="wasteDisposalAmount" name="wasteDisposalAmount" value={item.wasteDisposalAmount} onChange={(e) => onCellPriceItemChange(e, lastRowIndex == index, item.itemIndex)} disabled={(item.statusId == 1 || item.statusId == 2) && item.typeId === 61 ? false : true} />
                                    </td>
                                    <td className="table__text--align">
                                        <input type='number' min="0" step="0.01" className='form-control allocate__size100 textalignright' id="fuelSurchargeAmount" name="fuelSurchargeAmount" value={item.fuelSurchargeAmount} onChange={(e) => onCellPriceItemChange(e, lastRowIndex == index, item.itemIndex)} disabled={(item.statusId == 1 || item.statusId == 2) ? false : true} />
                                    </td>
                                    <td className={systemCompanyId === 2 ? "table__text--align" : "hidden"}>
                                        <input type='number' min="0" step="1" className='form-control textalignright table-input--minwidth' value={item.extraHours} onChange={(e) => onCellChange(lastRowIndex == index, item.itemIndex, "extraHours", Number(e.target.value))} disabled={(item.statusId == 1 || item.statusId == 2) && item.itemId != 4 && item.itemId != 25 && item.itemId != 33 && !item.isWalkWay ? false : true} />
                                    </td>
                                    <td className="hidden">{item.rate}</td>
                                    <td className="hidden">{item.gst}</td>
                                    <td className="table__text--align"><input type='number' min="0" step="0.01" className='form-control textalignright table-input--minwidth100' value={item.amount} onChange={(e) => onCellChangeNumber(lastRowIndex == index, item.itemIndex, "amount", e.target.value)} disabled={item.statusId == 1 || item.statusId == 2 ? false : true} /></td>
                                    <td className="table__text--align"><input type='number' min="0" step="0.01" className='form-control textalignright table-input--minwidth' value={item.overhireRate} onChange={(e) => onCellChangeNumber(lastRowIndex == index, item.itemIndex, "overhireRate", e.target.value)} disabled={item.statusId == 1 || item.statusId == 2 ? false : true} /></td>
                                    <td className={systemCompanyId === 2 ? "table__text--align textalignright" : "hidden"}>
                                        {item.estCostPriceFormatted}
                                    </td>
                                    <td className={systemCompanyId === 2 ? "table__text--align textalignright" : "hidden"}>
                                        {item.estMarginFormatted}
                                    </td>
                                    <td className={systemCompanyId === 2 ? "table__text--align textalignright" : "hidden"}>
                                        {item.estMarginPercentageFormatted }
                                    </td>
                                    <td className="table__text--align">
                                        {(item.statusId == 1 || item.statusId == 2) && lastRowIndex != index ?
                                            <div className="delete--tablecell tooltipitem tooltipitem__left">
                                                <a className="makeitred" href="#" onClick={(e) => cancelQuoteItem(e, item.itemIndex)}>
                                                    <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                                    <span className="tooltipitemtext tooltipitemtext__left">Remove item from Quote</span>
                                                </a>
                                            </div> : ""
                                        }
                                    </td>
                                    <td className="table__text--align">
                                        <a className={!item.notes ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editItemNotes(e, item.itemIndex)}>
                                            <div className="tooltipitem tooltipitem__left">{item.notes ? item.notes.substring(0, 20) + "... " : ""}
                                                <span className="tooltipitemtext tooltipitemtext__large tooltipitemtext__left">{item.notes}</span>
                                            </div>
                                        </a>
                                        <a className={!item.notes && lastRowIndex != index && item.id > 0 ? "icon--cellwidth tooltipitem tooltipitem__left" : "hidden"} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editItemNotes(e, item.itemIndex)}>
                                            <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                            <span className="tooltipitemtext tooltipitemtext__left">Add/Edit Notes</span>
                                        </a>
                                    </td>
                                </tr>
                                    
                        )}
                        </tbody>
                    </table>
                    {errors["quoteItems"] ?
                        (errors["quoteItems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
                <div className="makeitflexspacebetween">
                    <label className={systemCompanyId === 3 ? "hidden" : "input-group--inline job__checkboxPosition"} htmlFor="showSiteVisitNote">
                        <span className="label">Job Quote - include Site Visit note</span>
                        <input type="checkbox" name="showSiteVisitNote" id="showSiteVisitNote" checked={quoteDetails.showSiteVisitNote} onChange={(e) => handleSiteVisitNoteChange(e)}></input>
                    </label>
                    <div className="marginTop10 alignitemscentre">
                        <span className="marginRight10">Upload Plans</span> <input type="file" name="file" accept="application/pdf" onChange={handleImportFile} multiple></input>
                    </div>
                    <div className="alignitemscentre">
                        <div className={plans.length > 0 ? "" : "hidden"}>
                            {plans.map(plan =>
                                <div key={plan.id}>
                                    <a href="#" onClick={(e) => downloadPlan(e, plan.id)}>{plan.fileName}</a>
                                    <button type="button" className="marginLeft10 close makeitred" onClick={() => deleteJobPlan(plan.id)} >&times;</button>
                                </div>
                            )}
                                
                        </div>
                    </div>
                    <div className="input-group--inline job__quote--total--nomargin">
                        <span className="label-large job__quote--total">Total Amount Quoted</span>
                        <span className="job__quote--amount makeitflexright">{quoteDetails.totalQuotedAmountFormatted}</span>
                    </div>
                </div>
                <div className={systemCompanyId === 3 ? "makeitflexright" : "hidden"}>
                    <div className="input-group--inline job__quote--total--nomargin">
                        <a className="marginRight10" href="#" onClick={(e) => viewExtraInvoiceItems(e)}>Edit Extras</a>
                        <span className="label-large job__quote--total">Extra Invoice Amount</span>
                        <span className="job__quote--amount makeitflexright">{quoteDetails.totalInvoiceExtraAmountFormatted}</span>
                    </div>
                </div>
                <div className={systemCompanyId === 3 ? "makeitflexright" : "hidden"}>
                    <div className="input-group--inline job__quote--total--nomargin">
                        <span className="label-large job__quote--total">Total Invoice Amount</span>
                        <span className="job__quote--amount makeitflexright">{quoteDetails.totalInvoiceAmountFormatted}</span>
                    </div>
                </div>
                <div className="input-group-parent--row">
                    <button type="button" className={systemCompanyId === 3 ? "hidden" : "defaultbutton marginRight10"} onClick={addSiteNotes}>Add Site Notes</button>
                    <button className="defaultbutton" type="button" onClick={printJobQuote}>{jobQuoteHeading}</button>
                    <button className={systemCompanyId === 3 && hasEpaOtherItems ? "defaultbutton marginLeft10" : "hidden"} type="button" onClick={printJobQuoteSampleTesting}>Print Sample Testing</button>
                    <button className={systemCompanyId === 3 ? "hidden" : "defaultbutton marginLeft10"} type="button" onClick={printJobQuoteAcceptance} disabled={customerAccountTypeId != 1}>Print Acceptance</button>
                </div>
                <div className="input-group-parent">
                    <div className="colAlignRight">
                        <button className="defaultbutton defaultbutton__small marginRight10" type="button" onClick={copyJob}>Copy Job</button>
                        <button className="defaultbutton defaultbutton--save" type="button" onClick={submit}>Save</button>
                    </div>
                </div>
            </div>
        </div>
    )

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    );
}

export default JobQuoteData;